import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * German-first interface, for this page only.
 *
 * A DELIBERATE EXCEPTION to the project's English-UI rule, and it is written down here so the
 * next session does not "correct" it back: this page belongs to one accountant, it reproduces a
 * German federal form, and half its vocabulary (Kennzahl, Bemessungsgrundlage, Vorsteuer) has no
 * useful English form anyway. German is the DEFAULT; English is the fallback for everyone else
 * who opens it.
 *
 * No i18n framework for one page - that would be a dependency, a build step and a lazy-loading
 * question in exchange for two dictionaries. This is the two dictionaries.
 *
 * localStorage, not sessionStorage: the language has to survive a browser restart, where the
 * month selection deliberately does not. It is per browser profile, so somebody who opens the
 * page on a second machine sets it once more - a server-side user preference would be its own
 * table and is not worth it here.
 */

export type VatLang = 'de' | 'en';

const STORAGE_KEY = 'vatReporting.lang';
const DEFAULT_LANG: VatLang = 'de';

function loadLang(): VatLang {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw === 'en' || raw === 'de' ? raw : DEFAULT_LANG;
    } catch {
        // Private mode, or a browser set to block site data. The page still has to render.
        return DEFAULT_LANG;
    }
}

interface VatLangValue {
    lang: VatLang;
    setLang: (l: VatLang) => void;
    /** Look up a string; `de` wins, English is the fallback, the key itself is the last resort. */
    t: (key: string, params?: Record<string, string | number>) => string;
    /**
     * Same, but falls back to a ready-made sentence when the key is unknown. Used for the
     * controls: the server always ships an English `detail`, so a check added tomorrow reads
     * correctly today instead of printing its own lookup key at the user.
     */
    tOr: (key: string, fallback: string, params?: Record<string, string | number>) => string;
}

const VatLangContext = createContext<VatLangValue | null>(null);

export function VatLangProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState<VatLang>(loadLang);

    useEffect(() => {
        try { window.localStorage.setItem(STORAGE_KEY, lang); }
        catch { /* nothing to do - the choice simply does not persist */ }
    }, [lang]);

    const setLang = useCallback((l: VatLang) => setLangState(l), []);

    const t = useCallback((key: string, params?: Record<string, string | number>) => {
        const table = lang === 'de' ? DE : EN;
        const raw = table[key] ?? EN[key] ?? key;
        if (!params) return raw;
        return raw.replace(/\{(\w+)\}/g, (m, p) => (params[p] !== undefined ? String(params[p]) : m));
    }, [lang]);

    const tOr = useCallback((key: string, fallback: string, params?: Record<string, string | number>) => {
        const table = lang === 'de' ? DE : EN;
        const raw = table[key] ?? EN[key];
        if (raw === undefined) return fallback;
        return params
            ? raw.replace(/\{(\w+)\}/g, (m, p) => (params[p] !== undefined ? String(params[p]) : m))
            : raw;
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, t, tOr }), [lang, setLang, t, tOr]);
    return <VatLangContext.Provider value={value}>{children}</VatLangContext.Provider>;
}

export function useVatLang(): VatLangValue {
    const ctx = useContext(VatLangContext);
    // A component rendered outside the provider still has to work rather than throw - it just
    // gets English, which is what every other page in the app is.
    if (!ctx) {
        return {
            lang: 'en',
            setLang: () => undefined,
            t: (key, params) => {
                const raw = EN[key] ?? key;
                return params
                    ? raw.replace(/\{(\w+)\}/g, (m, p) => (params[p] !== undefined ? String(params[p]) : m))
                    : raw;
            },
            tOr: (key, fallback, params) => {
                const raw = EN[key];
                if (raw === undefined) return fallback;
                return params
                    ? raw.replace(/\{(\w+)\}/g, (m, p) => (params[p] !== undefined ? String(params[p]) : m))
                    : raw;
            },
        };
    }
    return ctx;
}

/**
 * Render one `EcSalesLine.problems` entry.
 *
 * The server ships `key|param` rather than a sentence, because the same string is shown both in
 * the ZM panel and inside a control message and both are translated. An unknown key renders as
 * itself, which still says something, rather than disappearing.
 */
export function useEcProblemText() {
    const { t } = useVatLang();
    return (problem: string) => {
        const [key, param] = problem.split('|');
        return t(key, param === undefined ? undefined : { p: param });
    };
}

/**
 * Expand any `ecProblem.key|param` token embedded in a longer sentence.
 *
 * The VAT-id control joins several problems into one message server-side, so the keys arrive
 * inside prose rather than as a list. Anything the dictionary does not know is left exactly as
 * it stands - a visible key beats a silently emptied sentence.
 */
export function useProblemKeyExpander() {
    const { t } = useVatLang();
    return (text: string) =>
        text.replace(/ecProblem\.(\w+)(?:\|(\w+))?/g,
            (_m, key, param) => t(`ecProblem.${key}`, param ? { p: param } : undefined));
}

/** The switch itself. Two words, no dropdown - there are exactly two languages. */
export function VatLangToggle() {
    const { lang, setLang } = useVatLang();
    return (
        <div className="inline-flex rounded border overflow-hidden text-xs" role="group"
             aria-label={lang === 'de' ? 'Sprache' : 'Language'}>
            {(['de', 'en'] as const).map(l => (
                <button key={l}
                        onClick={() => setLang(l)}
                        aria-pressed={lang === l}
                        className={`px-2 py-1.5 font-medium transition-colors ${
                            lang === l
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                    {l.toUpperCase()}
                </button>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// The dictionaries.
//
// NOT TRANSLATED, ON PURPOSE: anything that comes out of BC (posting groups, document numbers,
// account names, counterparty names) and the Kennzahl descriptions, which are the official
// wording of a German federal form and are already German in both languages.
// ---------------------------------------------------------------------------------------------

const EN: Record<string, string> = {
    'page.title': 'VAT Reporting',
    'page.subtitle': 'Umsatzsteuer-Voranmeldung, Zusammenfassende Meldung and Intrastat, derived from the Business Central VAT entries.',
    'page.noAccess': 'You do not have access to this page.',
    'page.loading': 'Loading…',
    'page.company': 'Company',
    'page.month': 'Month',
    'page.year': 'Year',
    'tab.ustvaDe': 'VAT return Germany',
    'tab.entries': 'VAT entries',
    'tab.annualDe': 'Annual return',

    // --- the Jahresmeldung tab -----------------------------------------------------
    'annual.title': 'Annual VAT return {year}',
    'annual.identity': 'Tax number {taxNumber} · {vatId} · {office}',
    'annual.monthsFiled': '{filed} of 12 monthly returns filed',
    'annual.transferTicket': 'Transfer ticket',
    'annual.filedAt': 'Filed {date} by {by}',
    'annual.backfilled': 'Figures taken from the transmitted protocol: {source}',
    'annual.viewFiled': 'as filed',
    'annual.viewComputed': 'from the data',
    'annual.viewDiff': 'difference',
    'annual.matrixTitle': 'Twelve months per Kennzahl',
    'annual.matrixSubtitle': 'the columns Heike used to type off the twelve protocols',
    'annual.matrixLegend': 'Bases in whole euros, tax amounts to the cent. ° = month not filed yet. '
        + 'Click a figure to open its transactions.',
    'annual.diffLegend': 'Difference = today\'s data minus the filed figure, per month. '
        + '✓ = matches to the cent · a dot = month has no filed figure to compare against.',
    'annual.monthFiled': 'Monthly return filed',
    'annual.monthOpen': 'Monthly return not filed yet',
    'annual.formTitle': 'Umsatzsteuererklaerung {year}',
    'annual.formSubtitle': 'the annual form lines, under the year\'s own rounding rules',
    'annual.formLegend': 'Meldewert: the year\'s raw sum truncated to whole euros (bases) or to the cent '
        + '(tax amounts) - NOT the sum of the twelve truncated monthly figures. The bridge column shows '
        + 'the gap between the two; euros the monthly truncations dropped are recovered by the annual form.',
    'annual.colLine': 'Line',
    'annual.colLabel': 'Description',
    'annual.colRaw': 'From the data',
    'annual.colReported': 'Meldewert (EUR)',
    'annual.colMonthlySum': 'Σ monthly filings',
    'annual.colBridge': 'Bridge',
    'annual.colFiled': 'Filed (annual)',

    'checkTitle.ANNUAL_ALL_MONTHS_FILED': 'All twelve monthly returns filed',
    'check.ANNUAL_ALL_MONTHS_FILED.ok': 'All twelve monthly returns of this year are filed; the annual figures stand on complete ground.',
    'check.ANNUAL_ALL_MONTHS_FILED.warning': '{missingCount} month(s) not filed yet ({months}). The annual return cannot be completed before every month is; the sums cover the filed months only.',
    'check.ANNUAL_ALL_MONTHS_FILED.error': '{missingCount} month(s) of a closed year are not filed ({months}). The annual return cannot be completed before every month is.',
    'checkTitle.ANNUAL_FILED_VS_COMPUTED': 'Filed monthly figures against today\'s data, over the year',
    'check.ANNUAL_FILED_VS_COMPUTED.ok': 'Every filed monthly figure of this year matches what the data produces today.',
    'check.ANNUAL_FILED_VS_COMPUTED.warning': '{count} Kennzahl(en) differ between the filed months and today\'s data: {list}. Each cell of the difference view names its month.',
    'checkTitle.ANNUAL_ROUNDING_BRIDGE': 'Monthly truncation against annual truncation',
    'check.ANNUAL_ROUNDING_BRIDGE.none': 'Truncating the year\'s sum lands on the sum of the truncated months for every base.',
    'check.ANNUAL_ROUNDING_BRIDGE.bridge': 'The annual form truncates the YEAR\'s sum, so it recovers euros the monthly truncations dropped: {list}. Expected, not an error.',
    'checkTitle.ANNUAL_RETURN_FILED_VS_COMPUTED': 'The filed annual return against today\'s computation',
    'check.ANNUAL_RETURN_FILED_VS_COMPUTED.ok': 'Every line of the filed annual return matches what the data produces today.',
    'check.ANNUAL_RETURN_FILED_VS_COMPUTED.warning': '{count} line(s) of the filed annual return differ from today\'s computation: {list}.',
    'checkTitle.ANNUAL_UNRESOLVED_GROUPS': 'Posting-group combinations without a confirmed rule, over the year',
    'check.ANNUAL_UNRESOLVED_GROUPS.ok': 'Every combination that moved money this year is mapped to a Kennzahl or deliberately outside.',
    'check.ANNUAL_UNRESOLVED_GROUPS.warning': '{count} combination(s) moved money this year without a confirmed rule - resolve them on the settings tab.',
    'checkTitle.ANNUAL_RECONSTRUCTED_MONTHS': 'Months backfilled from ELSTER protocols',
    'check.ANNUAL_RECONSTRUCTED_MONTHS.warning': '{count} filed month(s) of this year are reconstructed: their figures come off the transmitted protocols, but their entry lists were read later, so a posting that arrived after filing is invisible to the monthly deltas.',

    // --- Phase B: status workflow, ELSTER entry help, year-boundary bridge ---
    'annualStatus.fromProtocol': 'from the transmitted protocol',
    'annualStatus.nothingRecorded': 'Nothing recorded for this year yet.',
    'annualStatus.markFiled': 'Record filing',
    'annualStatus.reopenHint': 'Reopens the year for editing. The transfer ticket and the figures already recorded as filed are kept.',
    'annualStatus.fileExplainer': 'Records that the annual return for {year} was transmitted, and freezes the line values as they stand. {filed} of twelve monthly returns are filed. The computed side is recalculated on the server at this moment; enter beside it what actually went into Mein ELSTER, so a difference stays visible instead of disappearing.',
    'annualStatus.ticket': 'Transfer ticket (annual return)',
    'annualStatus.filedValuesTitle': 'What was entered in ELSTER',
    'annualStatus.takeComputed': 'take the computed values',
    'annualStatus.recordFiling': 'Record filing',
    'annualStatus.notANumber': 'One of the filed figures is not a number - nothing was saved.',

    'elster.title': 'ELSTER entry help {year}',
    'elster.subtitle': 'the figures in the order of that year\'s form',
    'elster.copyAll': 'copy all',
    'elster.copyValue': 'copy {value}',
    'elster.groupType': 'Enter in Mein ELSTER',
    'elster.groupComputed': 'ELSTER calculates these - check them against ours',
    'elster.legend': 'The copied value is exactly what goes into the field: whole euros for the bases, euro and cent with a comma for the tax amounts, no thousands separators. The printed line numbers are this year\'s - the form renumbers between years.',

    'boundary.title': 'Across the year boundary {year}',
    'boundary.summary': '{inCount} posting(s) carried in ({inAmount}) · {outCount} carried out ({outAmount}) · {pendingCount} still in no declaration ({pendingAmount})',
    'boundary.colDirection': 'Direction',
    'boundary.colOrigin': 'Out of',
    'boundary.colTarget': 'Into',
    'boundary.kind.in': 'carried in',
    'boundary.kind.out': 'carried out',
    'boundary.kind.pending': 'nowhere yet',
    'boundary.nowhere': 'nowhere',
    'boundary.drillTitle': 'Posting',
    'boundary.legend': 'A posting whose own date lies in another year but which was carried into this one is INSIDE these figures; one carried out is not, although its date is. A deferred posting is in no declaration at all - the year is not final until it is placed. Every row opens the month that actually holds it.',

    'late.title': 'Postings after filing {year}',
    'late.summary': '{added} new, {changed} changed and {removed} reversed transaction(s) in the frozen months {months}.',
    'late.reconstructedNote': 'Month(s) {months} were reconstructed from the ELSTER protocol rather than frozen at filing, so anything that arrived before the backfill is already inside their snapshot.',
    'late.truncated': 'The list is capped; more rows exist.',
    'late.colKind': 'Movement',
    'late.colMonth': 'Month',
    'late.colImpact': 'Kennzahl effect',
    'late.kind.added': 'new',
    'late.kind.changed': 'changed',
    'late.kind.removed': 'reversed',
    'late.drillTitle': 'Posting',
    'late.removedHint': 'This posting is no longer in that month\'s list - that is what reversed means. It can still be seen in the frozen list on the Transactions tab ("as filed").',

    'checkTitle.ANNUAL_YEAR_BOUNDARY': 'Postings that moved across the year boundary',
    'check.ANNUAL_YEAR_BOUNDARY.none': 'No posting was carried into or out of this year by hand, and none of this year\'s postings is waiting to be placed.',
    'check.ANNUAL_YEAR_BOUNDARY.crossed': '{inCount} posting(s) dated in another year were carried INTO this one ({inAmount}), and {outCount} dated in this year were carried OUT ({outAmount}). The figures follow the decisions, not the dates: {list}',
    'check.ANNUAL_YEAR_BOUNDARY.pending': '{pendingCount} posting(s) of this year are deferred and in NO declaration at all ({pendingAmount}); {inCount} were carried in ({inAmount}) and {outCount} carried out ({outAmount}). The year is not final until every deferral is taken into a month or closed for good.',
    'checkTitle.ANNUAL_LATE_POSTINGS': 'Postings that arrived after a month of this year was filed',
    'check.ANNUAL_LATE_POSTINGS.ok': 'Nothing has moved in the {snapshotMonths} frozen month(s) of this year.',
    'check.ANNUAL_LATE_POSTINGS.moved': '{added} new, {changed} changed and {removed} reversed transaction(s) since filing, in month(s) {months} - {impact}. Anything that moves a Kennzahl belongs in the annual return, which is the correcting return for the year.',
    'check.ANNUAL_LATE_POSTINGS.reconstructedQuiet': 'Nothing has moved in the {snapshotMonths} frozen month(s) of this year - but {reconstructedCount} of them ({reconstructedMonths}) were reconstructed from the ELSTER protocol rather than frozen at filing, so a posting that arrived before the backfill is already inside the snapshot and cannot be detected here.',
    'check.ANNUAL_LATE_POSTINGS.reconstructedMoved': '{added} new, {changed} changed and {removed} reversed transaction(s) since filing, in month(s) {months} - {impact}. {reconstructedCount} frozen month(s) ({reconstructedMonths}) were reconstructed rather than frozen at filing, so this counts only what moved after the backfill.',
    'checkTitle.ANNUAL_PREPAYMENT_TOTAL': 'Vorauszahlungssoll against the twelve transmitted prepayments',
    'check.ANNUAL_PREPAYMENT_TOTAL.incomplete': 'Only {filedMonths} of twelve months are filed, so the Vorauszahlungssoll cannot be stated yet; the filed Kz 83 so far sum to {sum83}.',
    'check.ANNUAL_PREPAYMENT_TOTAL.derived': 'The twelve transmitted Kz 83 sum to {sum83}, the Sondervorauszahlung (Kz 39) is {sum39}, so the Vorauszahlungssoll for Zeile {line} is {derived}.',
    'check.ANNUAL_PREPAYMENT_TOTAL.ok': 'The filed Vorauszahlungssoll {filed} matches the twelve transmitted prepayments to the cent.',
    'check.ANNUAL_PREPAYMENT_TOTAL.off': 'The annual return was filed with {filed} in Zeile {line} while the twelve transmitted Kz 83 (plus Kz 39) come to {derived} - a gap of {gap}. That line is the credit for what has already been paid, so a wrong figure lands one-for-one on the final payment.',
    'checkTitle.ANNUAL_ZM_YEAR_TOTAL': 'The year\'s EC Sales control total against Zeile 38 + Zeile 59',
    'check.ANNUAL_ZM_YEAR_TOTAL.ok': 'The annual return declares {declared} of intra-Community supplies; the year\'s EC Sales lists come to {controlTotal} over {entries} entries - a gap of {gap}. The ZM rounds every entry UP while the return truncates one aggregate, so a gap inside that bound is expected and the tax office allows it.',
    'check.ANNUAL_ZM_YEAR_TOTAL.off': 'The annual return declares {declared} of intra-Community supplies but the year\'s EC Sales lists come to {controlTotal} - a gap of {gap} over {entries} entries, more than the per-entry rounding can open. The two declarations disagree about what was supplied.',
    'checkTitle.ANNUAL_FINAL_PAYMENT': 'The Abschlusszahlung against the G/L',
    'check.ANNUAL_FINAL_PAYMENT.zero': 'The filed Abschlusszahlung is {expected} - nothing for the G/L to settle.',
    'check.ANNUAL_FINAL_PAYMENT.matched': 'Document {document} on account {account}, posted {date}: {amountLcy} = {amountEur} EUR at that month\'s rate - matches the filed Abschlusszahlung of {expected} to the cent.',
    'check.ANNUAL_FINAL_PAYMENT.unmatched': 'Searched account(s) {accounts} for the filed Abschlusszahlung of {expected} and found {candidateCount} candidate posting(s), none matching within a few cents. The payment may not have posted yet, or sits on an account this company has not configured.',
    'check.ANNUAL_FINAL_PAYMENT.noAccount': 'No G/L account is configured to carry the {expected} Abschlusszahlung for this company (VatGlAccount, role \'annual\' or \'settlement\').',
    'check.ANNUAL_FINAL_PAYMENT.monthly': 'This company settles German VAT with the Finanzamt every month, not with one annual payment - there is no single {expected} posting for the G/L to carry. The twelve monthly settlements are not reconciled here (Phase D scope).',

    'status.open': 'Open',
    'status.reviewed': 'Reviewed',
    'status.filed': 'Filed',
    'status.markReviewed': 'Mark reviewed',
    'status.markFiled': 'Mark filed…',
    'status.reopen': 'Reopen',
    'status.nothingFrozen': 'Nothing frozen yet — a late posting into this month would be invisible.',

    'controls.title': 'Controls',
    'controls.allPassed': 'all {count} passed',
    'controls.needAttention': '{problems} of {count} need attention',
    'controls.showAll': 'Show all controls',
    'controls.showFindings': 'Show only findings',
    'controls.everyPassed': 'Every control passed for this period.',
    'controls.transactions': '{count} transaction(s) →',

    'ustva.title': 'Umsatzsteuer-Voranmeldung — {period}',
    'ustva.kz': 'Kz',
    'ustva.label': 'Description',
    'ustva.raw': 'From the data',
    'ustva.reported': 'Reported (EUR)',
    'ustva.items': 'Items',
    'ustva.computed': 'computed',
    'ustva.noTransactions': 'No transactions',
    'ustva.derivedFromOthers': 'Derived from the other Kennzahlen',
    'ustva.showDerivation': 'Show how this figure is built',
    'ustva.noDerivation': 'No derivation',
    'ustva.truncatedHint': 'Bases are truncated to whole euro',
    'ustva.truncatedBy': '{amount} dropped',
    'ustva.footnotePrefix': 'Kz 83 = output tax {outputTax}{breakdown} − input tax {deductibleTax} =',
    'ustva.footnoteSuffix': 'Bases are truncated to whole euro, the tax is recomputed from the truncated amount — that is how ELSTER calculates.',
    'ustva.taxOffice': 'Tax office',

    'deriv.sum': 'Sum from the data',
    'deriv.truncated': 'Truncated to whole euro',
    'deriv.toTheCent': 'Reported to the cent',
    'deriv.rate': 'Rate',
    'deriv.tax': 'Tax on the truncated base',
    'deriv.remaining': 'Remaining prepayment',
    'deriv.items': '({count} items)',
    'deriv.signReversed': 'sign reversed — BC posts sales negative',
    'deriv.dropped': '{amount} dropped — rounded down in the taxpayer\'s favour',
    'deriv.nothingDropped': 'nothing to drop',
    'deriv.inputNotRounded': 'input tax is never rounded',
    'deriv.fromRoundedBase': 'computed from the rounded base, not from the tax BC posted — this is how ELSTER calculates',
    'deriv.refund': 'negative = a refund',
    'deriv.outputTaxFrom': 'Output tax from Kz {kz} ({base} × {rate} %)',
    'deriv.lessInput': 'less input tax Kz {kz}',

    'groups.title': 'Posting-group totals',
    'groups.subtitle': 'the same breakdown as the BC report “MWST abrechnen und buchen”',
    'groups.busGroup': 'VAT business group',
    'groups.prodGroup': 'VAT product group',
    'groups.direction': 'Direction',
    'groups.empty': '(empty)',
    'groups.notReported': 'not reported',
    'groups.settlementNote': 'The settlement is posted as {docs} — {amount} CHF. It is excluded from every figure above; including it would net the month to zero.',

    'gl.title': 'VAT G/L accounts',
    'gl.subtitle': 'movement excluding the settlement posting — this is what the VAT entries must equal',
    'gl.account': 'Account',
    'gl.name': 'Name',
    'gl.operations': 'Operations CHF',
    'gl.settlement': 'Settlement CHF',
    'gl.postings': 'Postings',
    'gl.showPostings': 'Show postings',
    'gl.openInBc': 'Open the G/L entries for this document in Business Central',
    'gl.noDocumentNo': '(no document no.)',
    'gl.settlementExcluded': 'settlement — excluded from the figure above',
    'gl.postingCount': '{count} posting(s)',

    'delta.title': 'Since this period was filed',
    'delta.subtitle': '{count} transaction(s) frozen on {date}',
    'delta.nothingMoved': 'Nothing has moved. The filed return still matches the data.',
    'delta.effect': 'Effect on the return: {list}. A correcting return is needed for anything that moves a Kennzahl.',
    'delta.added': 'added',
    'delta.changed': 'changed',
    'delta.removed': 'removed',
    'delta.drillTitle': 'Transactions {kind} since filing',

    'drill.entry': 'Entry',
    'drill.date': 'Date',
    'drill.document': 'Document',
    'drill.type': 'Type',
    'drill.counterparty': 'Customer / vendor',
    'drill.partnerVatId': 'Partner VAT no.',
    'drill.groups': 'Groups',
    'drill.baseLocal': 'Base CHF',
    'drill.taxLocal': 'VAT CHF',
    'drill.baseReporting': 'Base EUR',
    'drill.taxReporting': 'VAT EUR',
    'drill.loading': 'Loading transactions…',
    'drill.empty': 'No transactions match this figure.',
    'drill.loadFailed': 'Failed to load entries',
    'drill.count': '{count} transaction(s)',
    'drill.showingFirst': '(showing the first {count})',
    'drill.settlement': '(settlement)',
    'drill.scan': 'Scan',
    'drill.noScan': 'no scan',
    'drill.scanTitle': 'Scanned original in DocuWare (match confidence {confidence})',
    'drill.scanUnverified': 'DocuWare link built from an unverified URL template',
    'drill.salesNotScanned': 'Sales invoices are produced by BC and are not archived in DocuWare',
    'drill.noScanLinked': 'No DocuWare document is linked to this posting',

    'ec.title': 'Zusammenfassende Meldung',
    'ec.download': 'ELSTER CSV',
    'ec.controlTotal': 'Control total',
    'intrastat.title': 'Intrastat',
    'intrastat.download': 'IDEV CSV',
    'intrastat.review': 'Review list (xlsx)',
    'intrastat.uploadHint': 'Upload the IDEV CSV to IDEV exactly as downloaded. Do not open it in Excel and save it: Excel turns the codes "09" and "08" into "9" and "8", and IDEV rejects the import. To check the lines, use the review list.',
    'panel.loading': 'Loading…',
    'panel.downloadFailed': 'Download failed',
    'panel.notReconciled': 'Not yet reconciled against a transmitted file.',
    'panel.notReconciledHint': 'These figures are derived the same way as the return and reconcile against it, but this file has not been matched line by line against what was transmitted for this company. Check before transmitting.',
    'intrastat.zeroLines': '{count} row(s) with neither quantity nor value dropped',
    'gl.kind': 'Kind',
    'gl.amountChf': 'Amount CHF',
    'ecProblem.notEuPrefix': '\u201c{p}\u201d is not an EU member-state prefix',
    'ecProblem.ownVatId': 'this is our own VAT registration number',
    'ecProblem.domesticSupply': 'a supply to a {p} VAT id cannot be an intra-Community supply out of our {p} registration \u2014 it would be a domestic, taxable supply',
    'ec.roundingNote': 'Kz 41 is {kz41} against a ZM control total of {controlTotal}. The two are not meant to be equal: Kz 41 truncates one total, the ZM rounds each line up. A gap of up to {tolerance} EUR over {lines} line(s) is rounding, not an error.',
    'intrastat.noMovements': 'No commodity movements in this period.',
    'intrastat.missingTariff': '{count} line(s) of real goods have no commodity code.',
    'intrastat.andMore': 'and {count} more',
    'intrastat.missingTariffHint': 'They cannot be declared until the Warennummer is added to the item master.',
    'intrastat.excludedCharges': '{count} charge line(s) excluded (freight, VOC, surcharges \u2014 not commodity movements)',
    'intrastat.cancelled': '{count} shipment(s) cancelled by a credit note in the same month',
    'panel.loadFailedEc': 'Could not load the EC Sales List',
    'panel.loadFailedIntrastat': 'Could not load Intrastat',
    'panel.loadFailedReturn': 'Could not load the VAT return',
    'status.frozenCount': '{count} transaction(s) frozen',
    'status.reopenHint': 'Reopen the period. The frozen transaction list is discarded.',
    'status.changeFailed': 'Could not change the status',
    'status.fileAnyway': 'File anyway?',
    'status.filedBy': 'Filed {date} by {user}',
    'status.reviewedBy': 'Reviewed {date} by {user}',
    'status.ticket': 'ticket',
    'status.stillFailing': '{count} control(s) still report an error. Filing is not blocked, but the record will show that the month was filed with them open.',
    'status.freezeExplainer': 'Marking this period filed freezes all {count} transactions behind it. Anything posted into {period} afterwards will then be listed by document, so a correcting return can be written from it.',
    'status.ticketUstva': 'UStVA transfer ticket',
    'status.ticketZm': 'ZM transfer ticket',
    'status.optional': 'optional',
    'status.note': 'Note',
    'status.freezeAndFile': 'Freeze and mark filed',
    'status.cancel': 'Cancel',
    'status.reconstructedBadge': 'reconstructed',
    'status.reconstructedTitle': 'Reconstructed from the ELSTER protocol, not frozen at filing time',
    'status.reconstructedBody': 'The Kennzahl values below are the ones transmitted to the Finanzamt — they come from the Uebertragungsprotokoll. The transaction list was read from the replica afterwards, so a posting that arrived into this month after it was filed is already inside it and cannot be detected any more.',
    'status.reconstructedSource': 'Source',
    'status.reconstructedOn': 'Filed — figures reconstructed on {date}',

    // ---- the controls. Keyed by code + status; the server ships an English `detail` as the
    // fallback, so a check added later still reads correctly before anyone translates it.
    'checkTitle.GL_RECONCILIATION': 'VAT entries against the VAT G/L accounts',
    'checkTitle.GL_RECONCILIATION_INPUT': 'Input tax: VAT entries against account {accounts}',
    'checkTitle.GL_RECONCILIATION_OUTPUT': 'Output tax: VAT entries against account {accounts}',
    'checkTitle.EC_SALES_CONTROL_TOTAL': 'The EC Sales List control total against the return',
    'checkTitle.UNRESOLVED_GROUPS': 'Posting-group combinations that reach no Kennzahl',
    'checkTitle.EC_SALES_MISSING_VATID': 'Exempt supplies without a customer VAT number',
    'checkTitle.EC_SALES_VATID_SUSPECT': 'Customer VAT numbers',
    'checkTitle.LATE_POSTINGS': 'Postings that arrived after this period was filed',
    'checkTitle.FILED_VS_COMPUTED': 'What was filed against what the data says now',
    'checkTitle.PRIOR_MONTH': 'Movement against the previous month',
    'checkTitle.ROUNDING': 'Rounding applied to the reported figures',
    'checkTitle.DATA_FRESHNESS': 'Replica coverage',
    'checkTitle.REPORTING_CURRENCY': 'Reporting-currency amounts',

    'check.GL_RECONCILIATION.unavailable': 'No postings found on the configured VAT accounts for this period. Either the month has not been posted yet, or the wrong accounts are configured.',
    'check.GL_RECONCILIATION_INPUT.ok': 'VAT entries and the G/L agree at {entries} (settlement posting excluded on both sides).',
    'check.GL_RECONCILIATION_INPUT.error': 'VAT entries say {entries}, the G/L says {gl} \u2014 a difference of {diff}. Something was posted to a VAT account without a VAT entry, or to a VAT entry without touching the account.',
    'check.GL_RECONCILIATION_OUTPUT.ok': 'VAT entries and the G/L agree at {entries} (settlement posting excluded on both sides).',
    'check.GL_RECONCILIATION_OUTPUT.error': 'VAT entries say {entries}, the G/L says {gl} \u2014 a difference of {diff}. Something was posted to a VAT account without a VAT entry, or to a VAT entry without touching the account.',
    'check.EC_SALES_CONTROL_TOTAL.ok': 'The return reports {reported}, the ZM control total is {controlTotal}. The {gap} gap is rounding: the return truncates one total, the ZM rounds each of {lines} lines up. Expected, and the tax office allows it.',
    'check.EC_SALES_CONTROL_TOTAL.error': 'The return reports {reported} but the ZM control total is {controlTotal}. A {gap} gap is more than rounding can explain over {lines} line(s) (at most {tolerance}). The tax office reconciles these two figures.',
    'check.ecFilesUnverified': 'The EC Sales List is not offered as a file for this company yet, so it is not reconciled here either. Measured over 2026: the list built from these entries lands on Kz 41 + Kz 42 within the per-line rounding every month, while the transmitted files do not \u2014 which is why matching them is not the test.',
    'check.ecNoCurrency': 'The EC Sales List cannot be derived for this company \u2014 its BC replica carries no foreign-currency amounts, and the ZM is filed in euro.',
    'check.UNRESOLVED_GROUPS.error': '{count} combination(s) moved money this period but reach no Kennzahl: {list}. Each is either missing from the rule table or marked pending there. Nothing is silently dropped \u2014 but nothing is reported either.',
    'check.UNRESOLVED_GROUPS.ok': 'Every combination that moved money this period maps to a Kennzahl.',
    'check.EC_SALES_MISSING_VATID.error': '{count} entr(y/ies) totalling {amount} are booked as exempt intra-Community supplies but carry no customer VAT registration number. They cannot go into the ZM, and without a valid number the exemption does not hold.',
    'check.EC_SALES_VATID_SUSPECT.error': '{list}',
    'check.EC_SALES_VATID_SUSPECT.ok': 'All {count} EC Sales line(s) carry a plausible EU VAT number.',
    'check.LATE_POSTINGS.ok': 'Nothing has moved since the return was filed: all {snapshot} transaction(s) are unchanged.',
    'check.LATE_POSTINGS.error': '{added} new, {changed} changed and {removed} reversed transaction(s) since filing \u2014 {impact}. A correcting return is needed for anything that moves a Kennzahl.',
    'check.LATE_POSTINGS.warning': 'This period is marked filed but carries no transaction snapshot, so a late posting cannot be detected. Snapshots start when a period is filed through this page.',
    'check.LATE_POSTINGS.reconstructedQuiet': 'This period was reconstructed from the ELSTER protocol, not frozen when it was filed. Its {snapshot} transaction(s) were read from the replica afterwards, so anything that arrived between the filing and the reconstruction is already inside them and cannot be detected here. Nothing has moved since the reconstruction.',
    'check.LATE_POSTINGS.reconstructedMoved': 'This period was reconstructed from the ELSTER protocol, not frozen when it was filed, so a posting that arrived before the reconstruction is already inside its {snapshot} transaction(s) and cannot be detected. Since the reconstruction: {added} new, {changed} changed and {removed} reversed transaction(s) — {impact}.',
    'check.FILED_VS_COMPUTED.ok': 'Every filed figure still matches the current data.',
    'check.FILED_VS_COMPUTED.warning': 'Filed / current: {list}. Either a figure was adjusted by hand in ELSTER, or the underlying postings changed.',
    'check.FILED_VS_COMPUTED.unfiledOnly': 'Every filed figure still matches — but the data now also produces {unfiledList}, which the filing does not carry at all. A rule was resolved after this month was filed, or postings were reclassified into a Kennzahl that was never declared.',
    'check.FILED_VS_COMPUTED.bothWarning': 'Filed / current: {list}. On top of that the data now produces {unfiledList}, which the filing does not carry at all — a rule resolved after the filing, or postings reclassified into a Kennzahl that was never declared.',
    'check.PRIOR_MONTH.ok': 'No Kennzahl moved by more than {threshold} % against the previous month.',
    'check.PRIOR_MONTH.warning': '{list}. Large swings are normal in this business \u2014 this is a prompt to look, not a verdict.',
    'check.ROUNDING.ok': '{list}. Bases are truncated to whole euro and the tax is recomputed from the truncated base, which is what ELSTER does.',
    'check.DATA_FRESHNESS.ok': 'The replica carries postings up to {loadedTo}.',
    'check.DATA_FRESHNESS.error': 'The newest posting in the replica is {loadedTo}, before the end of this period ({periodEnd}). The month is not fully loaded \u2014 do not file on these figures.',
    'check.DATA_FRESHNESS.unavailable': 'Could not determine how far the replica is loaded.',
    'check.REPORTING_CURRENCY.unavailable': 'This company\u2019s BC replica carries neither a document-currency nor an additional reporting-currency amount on its VAT entries, so the euro Kennzahlen cannot be derived here.',

    // ---- the Excel-style column filter, used in every drill-down table
    'filter.search': 'Search\u2026',
    'filter.all': 'All',
    'filter.none': 'None',
    'filter.from': 'from',
    'filter.to': 'to',
    'filter.empty': '(blank)',
    'filter.active': '{count} filter(s) active',
    'filter.clearAll': 'Clear all',
    'drill.filteredCount': '{shown} of {total} transaction(s)',

    // ---- the "VAT entries" tab - the full basis behind the calculation
    'entries.viewFrozen': 'Frozen filing',
    'entries.viewCurrent': 'Current data',
    'entries.includeSettlement': 'Include settlement posting',
    'entries.reconstructedBanner': 'This list was reconstructed from the ELSTER protocol after the fact, not frozen when the return was filed \u2014 a posting that arrived before the reconstruction is already inside it and would not show up as a difference.',
    'entries.notCaptured': 'not captured in the freeze',
    'entries.diffIntro': 'Marked below, against the frozen list:',
    'entries.reportingDate': 'Reporting date',
    'entries.postingDate': 'Posting date',
    'entries.country': 'Country',
    'entries.disposition': 'Disposition',
    'entries.settlementCol': 'Settlement',
    'entries.empty': 'No VAT entries for this period.',
    'entries.unavailableFrozen': 'This period has no frozen transaction list \u2014 it was never filed through this page, or it was reopened since.',
    'panel.loadFailedEntries': 'Could not load the VAT entries',

    // ---- the "Einstellungen" tab - the five config tables, editable (Phase 4)
    'tab.settings': 'Settings',
    // --- manual exclusions and carry-forward (Phase 5) -------------------------------------
    //
    // The reach of the decision - all three declarations at once - is repeated on every dialog
    // rather than documented once: it is the thing a person is most likely to assume wrongly.
    'excl.column': 'Reporting',
    'excl.exclude': 'do not report',
    'excl.excludeHint': 'Take this posting out of the UStVA, the ZM and Intrastat for this month, with a reason.',
    'excl.reset': 'undo',
    'excl.resetHint': 'Withdraw the decision — the posting goes back into the month its own date puts it in. The history keeps the record.',
    'excl.periodFiled': 'This period has been filed. A posting that has to move out of a filed month needs a correcting return, not an exclusion.',
    'excl.periodFiledShort': 'filed',
    'excl.state.deferred': 'not reported',
    'excl.state.included': 'carried forward',
    'excl.state.dropped': 'never reported',
    'excl.tooltipBy': '{who}, {when}, out of {period}',
    'excl.tooltipInto': 'carried into {period}',
    'excl.title.exclude': 'Take this posting out of the declaration',
    'excl.title.include': 'Carry this posting into the declaration',
    'excl.title.drop': 'Never declare this posting',
    'excl.title.reset': 'Withdraw the decision',
    'excl.intro.exclude': 'The posting stays in Business Central and stays visible here — it simply does not go into the {period} declarations. Say why.',
    'excl.intro.include': 'The posting was deferred in {origin}. It goes into the {period} declarations in full, from now on.',
    'excl.intro.drop': 'The posting was deferred in {origin} and will never be declared. The decision and its reason stay on the record.',
    'excl.intro.reset': 'The posting goes back into {origin}, the month its own date puts it in. The decision stays in the history as one that was made and withdrawn.',
    'excl.placeholder.exclude': 'e.g. still being clarified with the supplier',
    'excl.placeholder.include': 'e.g. clarified — the invoice is correct',
    'excl.placeholder.drop': 'e.g. posted in error, reversed in BC',
    'excl.confirm.exclude': 'Do not report',
    'excl.confirm.include': 'Carry forward',
    'excl.confirm.drop': 'Never declare',
    'excl.confirm.reset': 'Withdraw',
    'excl.cancel': 'Cancel',
    'excl.commentLabel': 'Reason',
    'excl.commentHint': '{left} characters left. This is what the control and the history will show.',
    'excl.failed': 'The decision could not be saved.',
    'excl.filedRefusal.exclude': '{period} has been filed. A posting that has to move out of a filed month needs a correcting return, not an exclusion.',
    'excl.filedRefusal.include': '{period} has been filed and can take nothing in. Carry the posting into an open month.',
    'excl.filedRefusal.drop': 'A filed month stands in the way of this decision. That needs a correcting return, not an exclusion.',
    'excl.filedRefusal.reset': 'Withdrawing this decision would change a filed month (the month of origin {origin}, or the month the posting was carried into). That needs a correcting return.',
    'excl.package': 'The decision covers all three declarations at once — UStVA, Zusammenfassende Meldung and Intrastat. There is no way to exclude a posting from one of them only.',
    'excl.effect': 'Effect',
    'excl.effectValue': 'Kz {kz} {sign} {amount}',
    'excl.effectNone': 'reaches no Kennzahl',
    'excl.signUp': 'rises by',
    'excl.signDown': 'falls by',
    'excl.signBack': 'moves by',
    'excl.effectDrop': 'Kz {kz} — stays in no declaration',
    'excl.originReason': 'Deferred because:',

    'carry.title': 'Postings carried over from earlier periods',
    'carry.subtitle': '{count} waiting, {amount} — the oldest for {oldest} month(s)',
    'carry.origin': 'From',
    'carry.age': 'Age',
    'carry.months': '{count} mo.',
    'carry.contribution': 'Effect',
    'carry.comment': 'Reason',
    'carry.actions': 'Decision',
    'carry.include': 'take into {period}',
    'carry.drop': 'never declare',
    'carry.reset': 'undo',
    'carry.resetHint': 'Withdraw the deferral — the posting goes back into {period}.',
    'carry.entryMissing': 'no longer in the replica',
    'carry.periodFiled': 'The month on screen has been filed and can take nothing in.',
    'carry.periodFiledShort': 'month filed',
    'carry.readOnly': 'read only',

    'checkTitle.MANUAL_EXCLUSIONS': 'Postings taken out of this return by hand',
    'check.MANUAL_EXCLUSIONS.ok': 'Nothing was excluded from this period by hand, and nothing was carried into it.',
    'check.MANUAL_EXCLUSIONS.excluded': '{excludedCount} posting(s) were taken out of this return by hand, worth {excludedAmount} in their Kennzahlen: {excludedList}. The decision covers the UStVA, the ZM and Intrastat together.',
    'check.MANUAL_EXCLUSIONS.carried': '{carriedCount} posting(s) from an earlier month were carried into this one, worth {carriedAmount}: {carriedList}. They are inside every figure on this page.',
    'check.MANUAL_EXCLUSIONS.both': '{excludedCount} posting(s) were taken out of this return by hand ({excludedAmount}): {excludedList}. {carriedCount} posting(s) from an earlier month were carried into it ({carriedAmount}): {carriedList}. Both decisions cover all three declarations.',

    'checkTitle.CARRY_FORWARD_OPEN': 'Postings from earlier months that are in no declaration',
    'check.CARRY_FORWARD_OPEN.ok': 'No posting from an earlier month is waiting to be declared.',
    'check.CARRY_FORWARD_OPEN.warning': '{count} posting(s) deferred in an earlier month are still in no declaration at all, worth {amount}; the oldest has been waiting {oldest} month(s): {list}. Each has to be taken into a return or closed for good — until then it is declared nowhere.',
    'config.retroWarning': 'These rules are applied when a return is COMPUTED, not when it was filed. A change here therefore moves every month that is still open - and it also moves the computed side of every month already filed: what a filed month froze is the figures that went to the Finanzamt and its transaction list, not the comparison against them. Each save says how far back it reaches.',
    'config.pendingHint': '{count} posting-group combination(s) are still marked "pending". They move money and reach no Kennzahl, which is reported as a finding rather than silently dropped - resolving them here is what this tab is for.',
    'config.loadFailed': 'The configuration could not be loaded.',
    'config.created': 'Row created. The change is in the history with its before and after.',
    'config.updated': 'Row updated. The change is in the history with its before and after.',
    'config.deleted': 'Row deleted. Its contents are kept in the history.',

    'config.section.line-map.title': 'Kennzahl mapping',
    'config.section.line-map.description': 'Which combination of business posting group, product posting group and direction reaches which Kennzahl. A combination with no row, or one marked "pending", is reported as a finding - never as a silent zero.',
    'config.section.scope.title': 'Scope',
    'config.section.scope.description': 'Which business posting groups belong to this return at all. This is the filter that used to be an AutoFilter setting.',
    'config.section.scope.warning': 'Every VAT-entry query joins this table. Removing a group takes its postings out of the UStVA, the EC Sales List and Intrastat at once; adding one brings them into all three.',
    'config.section.country.title': 'Filing identity',
    'config.section.country.description': 'Tax number, own VAT id, tax office, the standard rate, and the four Intrastat constants.',
    'config.section.country.warning': 'The whole return hangs off this row: with none, the page cannot compute anything for this company. The four Intrastat constants (11 / 3 / 08 / 99) were confirmed by Heike as always applying.',
    'config.section.gl-account.title': 'VAT G/L accounts',
    'config.section.gl-account.description': 'The accounts the month is reconciled against - the VAT entries of a period must equal the movement on these accounts.',
    'config.section.own-registration.title': 'Own VAT registrations',
    'config.section.own-registration.description': 'Which of our own VAT numbers a posting group runs under.',
    'config.section.own-registration.warning': 'This table is shared. The DocuWare purchase-order check reads it too (BILLTO_VATID_MISMATCH), so a change here also changes which number that checker expects to see printed on a supplier invoice. Its key is company plus posting group - the country is an ordinary column, which is why all countries of this company are listed here, not only the one selected above.',

    'config.rowCount': '{count} row(s)',
    'config.filteredCount': '{shown} of {total} row(s)',
    'config.new': 'New row',
    'config.empty': 'No rows yet.',
    'config.actions': 'Actions',
    'config.edit': 'Edit',
    'config.duplicate': 'Duplicate onto another key',
    'config.delete': 'Delete',
    'config.yes': 'yes',
    'config.no': 'no',
    'config.emptyValue': '(empty)',
    'config.notSet': 'not set',
    'config.emptyAllowed': 'may be empty',

    'config.dialogCreate': 'New row',
    'config.dialogEdit': 'Edit row',
    'config.keyMarker': '(key)',
    'config.keyLocked': 'The key fields are the identity of the row, so they cannot be edited. To move a rule onto another combination, duplicate it and delete the old one.',
    'config.rulesUnmet': 'Not saveable yet',
    'config.rule.mappedNeedsKennzahl': 'A mapped row needs a Kennzahl - without one it contributes nothing and no check would say so.',
    'config.rule.mappedNeedsLineRole': 'A mapped row needs a line role: it decides whether the base or the tax amount is reported, and how the tax is derived.',
    'config.rule.acquisitionNeedsAcqKennzahl': 'An acquisition needs the Kennzahl for its deductible side (normally 61). Business Central posts no tax amount on these entries, so that figure exists only as trunc(base) x rate.',
    'config.rule.acquisitionNeedsRate': 'An acquisition needs a tax rate, or its deductible side cannot be computed.',
    'config.rule.outputTaxableNeedsRate': 'A taxable output row needs a tax rate. Without it the base reaches its Kennzahl but no output tax is computed, and Kz 83 comes out too low with nothing to show for it.',
    'config.cancel': 'Cancel',
    'config.save': 'Save',

    'config.impactTitle': 'What this change touches',
    'config.impactNone': 'No postings on this combination since {from}. The change affects nothing that has been booked so far.',
    'config.impactSummary': '{months} month(s) since {from} carry postings on this combination, {entries} VAT entr(ies) in total: {open} still open, {filed} already filed.',
    'config.impactFiledNote': 'The filed months keep the figures that were transmitted. What changes for them is the computed comparison - "filed against computed" - which is frequently the whole point.',
    'config.impactPeriodTitle': '{entries} entr(ies) - {status}',

    'config.historyTitle': 'History',
    'config.historyEmpty': 'Nothing has been changed through this page yet. Changes made directly in the database are deliberately not recorded here - this trail is what the app did.',
    'config.historyNoFieldChange': 'Saved without any field changing.',
    'config.historyTruncated': 'Only the most recent entries are shown.',
    'config.action.INSERT': 'created',
    'config.action.UPDATE': 'changed',
    'config.action.DELETE': 'deleted',

    'config.field.companyCode': 'Company',
    'config.field.countryCode': 'Country',
    'config.field.vatBusPostingGroup': 'Business posting group',
    'config.field.vatProdPostingGroup': 'Product posting group',
    'config.field.direction': 'Direction',
    'config.field.disposition': 'Disposition',
    'config.field.lineRole': 'Line role',
    'config.field.kennzahl': 'Kennzahl',
    'config.field.kennzahlAcqTax': 'Kz acquisition tax',
    'config.field.taxRatePct': 'Rate %',
    'config.field.sign': 'Sign',
    'config.field.ecSalesType': 'ZM type',
    'config.field.includeInIntrastat': 'Intrastat',
    'config.field.note': 'Note',
    'config.field.taxNumber': 'Tax number',
    'config.field.ownVatRegistrationNo': 'Own VAT id',
    'config.field.taxOffice': 'Tax office',
    'config.field.standardRatePct': 'Standard rate %',
    'config.field.intrastatNatureOfTransaction': 'Nature of transaction',
    'config.field.intrastatModeOfTransport': 'Mode of transport',
    'config.field.intrastatRegionDomestic': 'Region domestic',
    'config.field.intrastatRegionForeign': 'Region foreign',
    'config.field.glAccountNo': 'G/L account',
    'config.field.accountRole': 'Account role',
    'config.field.accountName': 'Account name',
    'config.field.vatRegistrationNo': 'VAT number',

    'config.value.direction.Einkauf': 'Einkauf (purchase)',
    'config.value.direction.Verkauf': 'Verkauf (sale)',
    'config.value.disposition.mapped': 'mapped - sums into its Kennzahl',
    'config.value.disposition.not_reported': 'not_reported - deliberately outside the return',
    'config.value.disposition.pending': 'pending - unanswered, reported as a finding',
    'config.value.lineRole.output_taxable': 'output_taxable - base to the Kennzahl, tax = trunc(base) x rate',
    'config.value.lineRole.output_exempt': 'output_exempt - base to the Kennzahl, no tax',
    'config.value.lineRole.acquisition': 'acquisition - base plus a deductible tax of the same amount',
    'config.value.lineRole.input_tax': 'input_tax - the TAX amount to the Kennzahl, deductible',
    'config.value.sign.1': '+1 (as booked)',
    'config.value.sign.-1': '-1 (flip the sign)',
    'config.value.ecSalesType.L': 'L - Lieferung',
    'config.value.ecSalesType.D': 'D - Dreiecksgeschaeft',
    'config.value.ecSalesType.S': 'S - sonstige Leistung',
    'config.value.accountRole.input': 'input tax',
    'config.value.accountRole.output': 'output tax',
    'config.value.accountRole.settlement': 'settlement',

    'config.deleteTitle': 'Delete this row?',
    'config.deleteBody': 'The row is removed from the configuration. It stays readable in the history, which is the only place its contents will still exist.',
    'config.deleteWarning': 'Every open month is recomputed without it, and the comparison against every filed month changes. A combination that then has no row at all is reported as a finding, not as a zero.',

    // Manual EUR correction (migration 229)
    'ovr.title': 'Correct {field}',
    'ovr.local': '{field}',
    'ovr.bcValue': 'BC figure',
    'ovr.inUse': 'Figure in use',
    'ovr.manual': 'manual',
    'ovr.newValue': 'New {field} (from the original document)',
    'ovr.notANumber': 'Not a number.',
    'ovr.signFlip': 'Opposite sign to BC. A rate correction keeps the sign BC books the posting with.',
    'ovr.deviation': 'Difference to BC: {amount} ({pct} %)',
    'ovr.commentLabel': 'Reason',
    'ovr.commentPlaceholder': 'e.g. rate per document 0.9412 instead of BC rate',
    'ovr.commentFirst': 'Enter a reason first',
    'ovr.package': 'The corrected figure applies to the VAT return, the EC Sales List and the annual return at once, and is frozen with the month when it is filed.',
    'ovr.otherColumn': 'Kz {kz} of this posting reads {other}. This correction does not change the VAT return.',
    'ovr.noKennzahl': 'This posting reaches no Kennzahl. This correction does not change the VAT return.',
    'ovr.readOnly': 'Read only: the month is filed or you have no edit right.',
    'ovr.driftLong': 'BC has changed since the correction: then {then}, now {now}. The correction still applies. Check whether it is still needed.',
    'ovr.save': 'Save correction',
    'ovr.reset': 'Reset to BC figure ({bc})',
    'ovr.close': 'Close',
    'ovr.failed': 'The correction could not be saved.',
    'ovr.history': 'History',
    'ovr.historyEmpty': 'No corrections so far.',
    'ovr.historyFailed': 'History could not be loaded.',
    'ovr.historySet': 'Corrected {from} → {to}',
    'ovr.historyReset': 'Reset {from} → BC {to}',
    'ovr.historyBc': 'BC {bc}',
    'ovr.cellCorrected': 'Corrected manually',
    'ovr.cellBc': 'BC: {bc}',
    'ovr.cellDrift': 'BC changed since: {then} → {now}',
    'ovr.cellBy': '{who}, {when}',
    'ovr.cellEditHint': 'Correct this euro figure manually',
};

const DE: Record<string, string> = {
    'page.title': 'Mehrwertsteuer',
    'page.subtitle': 'Umsatzsteuer-Voranmeldung, Zusammenfassende Meldung und Intrastat, abgeleitet aus den MWST-Buchungen in Business Central.',
    'page.noAccess': 'Sie haben keinen Zugriff auf diese Seite.',
    'page.loading': 'Wird geladen…',
    'page.company': 'Firma',
    'page.month': 'Monat',
    'page.year': 'Jahr',
    'tab.ustvaDe': 'Umsatzsteuer Deutschland',
    'tab.entries': 'Mehrwertsteuer Posten',
    'tab.annualDe': 'Jahresmeldung',

    // --- der Jahresmeldung-Tab -----------------------------------------------------
    'annual.title': 'Umsatzsteuer-Jahreserklärung {year}',
    'annual.identity': 'Steuernummer {taxNumber} · {vatId} · {office}',
    'annual.monthsFiled': '{filed} von 12 Monatsmeldungen gemeldet',
    'annual.transferTicket': 'Transferticket',
    'annual.filedAt': 'Gemeldet am {date} durch {by}',
    'annual.backfilled': 'Werte aus dem übermittelten Protokoll: {source}',
    'annual.viewFiled': 'gemeldet',
    'annual.viewComputed': 'aus den Daten',
    'annual.viewDiff': 'Differenz',
    'annual.matrixTitle': 'Zwölf Monate je Kennzahl',
    'annual.matrixSubtitle': 'die Spalten, die bisher aus den zwölf Protokollen abgetippt wurden',
    'annual.matrixLegend': 'Bemessungsgrundlagen in ganzen Euro, Steuerbeträge auf den Rappen. '
        + '° = Monat noch nicht gemeldet. Klick auf eine Zahl öffnet die Posten dahinter.',
    'annual.diffLegend': 'Differenz = heutige Daten minus Meldewert, je Monat. '
        + '✓ = stimmt auf den Rappen · Punkt = Monat hat keinen Meldewert zum Vergleichen.',
    'annual.monthFiled': 'Monatsmeldung übermittelt',
    'annual.monthOpen': 'Monatsmeldung noch offen',
    'annual.formTitle': 'Umsatzsteuererklärung {year}',
    'annual.formSubtitle': 'die Formularzeilen, mit den Rundungsregeln des Jahres',
    'annual.formLegend': 'Meldewert: die ungerundete Jahressumme abgerundet auf ganze Euro '
        + '(Bemessungsgrundlagen) bzw. centgenau (Steuerbeträge) — NICHT die Summe der zwölf '
        + 'gerundeten Monatswerte. Die Spalte «Brücke» zeigt die Lücke zwischen beiden; Euro, die '
        + 'die Monatsrundungen verloren haben, holt die Jahreserklärung zurück.',
    'annual.colLine': 'Zeile',
    'annual.colLabel': 'Bezeichnung',
    'annual.colRaw': 'Aus den Daten',
    'annual.colReported': 'Meldewert (EUR)',
    'annual.colMonthlySum': 'Σ Monatsmeldungen',
    'annual.colBridge': 'Brücke',
    'annual.colFiled': 'Gemeldet (Jahr)',

    'checkTitle.ANNUAL_ALL_MONTHS_FILED': 'Alle zwölf Monatsmeldungen übermittelt',
    'check.ANNUAL_ALL_MONTHS_FILED.ok': 'Alle zwölf Monatsmeldungen dieses Jahres sind übermittelt; die Jahreswerte stehen auf vollständigem Grund.',
    'check.ANNUAL_ALL_MONTHS_FILED.warning': '{missingCount} Monat(e) noch nicht gemeldet ({months}). Die Jahreserklärung kann erst nach dem letzten Monat fertiggestellt werden; die Summen decken nur die gemeldeten Monate.',
    'check.ANNUAL_ALL_MONTHS_FILED.error': '{missingCount} Monat(e) eines abgeschlossenen Jahres sind nicht gemeldet ({months}). Die Jahreserklärung kann erst nach dem letzten Monat fertiggestellt werden.',
    'checkTitle.ANNUAL_FILED_VS_COMPUTED': 'Gemeldete Monatswerte gegen die heutigen Daten, übers Jahr',
    'check.ANNUAL_FILED_VS_COMPUTED.ok': 'Jeder gemeldete Monatswert dieses Jahres stimmt mit dem überein, was die Daten heute ergeben.',
    'check.ANNUAL_FILED_VS_COMPUTED.warning': '{count} Kennzahl(en) weichen zwischen den gemeldeten Monaten und den heutigen Daten ab: {list}. Die Differenz-Ansicht benennt jeden Monat.',
    'checkTitle.ANNUAL_ROUNDING_BRIDGE': 'Monatsrundung gegen Jahresrundung',
    'check.ANNUAL_ROUNDING_BRIDGE.none': 'Die Abrundung der Jahressumme landet bei jeder Bemessungsgrundlage auf der Summe der gerundeten Monate.',
    'check.ANNUAL_ROUNDING_BRIDGE.bridge': 'Die Jahreserklärung rundet die JAHRES-Summe ab und holt damit Euro zurück, die die Monatsrundungen verloren haben: {list}. Erwartet, kein Fehler.',
    'checkTitle.ANNUAL_RETURN_FILED_VS_COMPUTED': 'Die übermittelte Jahreserklärung gegen die heutige Berechnung',
    'check.ANNUAL_RETURN_FILED_VS_COMPUTED.ok': 'Jede Zeile der übermittelten Jahreserklärung stimmt mit der heutigen Berechnung überein.',
    'check.ANNUAL_RETURN_FILED_VS_COMPUTED.warning': '{count} Zeile(n) der übermittelten Jahreserklärung weichen von der heutigen Berechnung ab: {list}.',
    'checkTitle.ANNUAL_UNRESOLVED_GROUPS': 'Buchungsgruppen-Kombinationen ohne bestätigte Regel, übers Jahr',
    'check.ANNUAL_UNRESOLVED_GROUPS.ok': 'Jede Kombination, die in diesem Jahr Geld bewegt hat, ist einer Kennzahl zugeordnet oder bewusst draussen.',
    'check.ANNUAL_UNRESOLVED_GROUPS.warning': '{count} Kombination(en) haben in diesem Jahr Geld bewegt, ohne dass eine Regel bestätigt ist — im Einstellungen-Tab auflösen.',
    'checkTitle.ANNUAL_RECONSTRUCTED_MONTHS': 'Aus ELSTER-Protokollen nachgetragene Monate',
    'check.ANNUAL_RECONSTRUCTED_MONTHS.warning': '{count} gemeldete Monat(e) dieses Jahres sind rekonstruiert: die Werte stammen aus den übermittelten Protokollen, die Postenlisten wurden aber später gelesen — eine Buchung nach der Meldung ist für die Monats-Deltas unsichtbar.',

    // --- Phase B: Status-Workflow, ELSTER-Erfassungshilfe, Jahresgrenzen-Brücke ---
    'annualStatus.fromProtocol': 'aus dem übermittelten Protokoll',
    'annualStatus.nothingRecorded': 'Für dieses Jahr ist noch nichts erfasst.',
    'annualStatus.markFiled': 'Meldung erfassen',
    'annualStatus.reopenHint': 'Öffnet das Jahr wieder zur Bearbeitung. Transferticket und die bereits als gemeldet erfassten Werte bleiben erhalten.',
    'annualStatus.fileExplainer': 'Hält fest, dass die Jahreserklärung {year} übermittelt wurde, und friert die Zeilenwerte im heutigen Stand ein. {filed} von zwölf Monatsmeldungen sind übermittelt. Die berechnete Seite wird in diesem Moment auf dem Server neu gerechnet; daneben wird eingetragen, was tatsächlich in Mein ELSTER erfasst wurde — damit eine Abweichung sichtbar bleibt statt zu verschwinden.',
    'annualStatus.ticket': 'Transferticket (Jahreserklärung)',
    'annualStatus.filedValuesTitle': 'In ELSTER erfasste Werte',
    'annualStatus.takeComputed': 'berechnete Werte übernehmen',
    'annualStatus.recordFiling': 'Meldung erfassen',
    'annualStatus.notANumber': 'Einer der gemeldeten Werte ist keine Zahl — es wurde nichts gespeichert.',

    'elster.title': 'ELSTER-Erfassungshilfe {year}',
    'elster.subtitle': 'die Werte in der Formularreihenfolge dieses Jahrgangs',
    'elster.copyAll': 'alles kopieren',
    'elster.copyValue': '{value} kopieren',
    'elster.groupType': 'In Mein ELSTER eintragen',
    'elster.groupComputed': 'Diese rechnet ELSTER selbst — gegen unsere prüfen',
    'elster.legend': 'Der kopierte Wert ist genau das, was ins Feld gehört: ganze Euro bei den Bemessungsgrundlagen, Euro und Cent mit Komma bei den Steuerbeträgen, ohne Tausendertrennzeichen. Die Zeilennummern sind die dieses Jahrgangs — das Formular nummeriert zwischen den Jahren um.',

    'boundary.title': 'Über die Jahresgrenze {year}',
    'boundary.summary': '{inCount} Posten hereingenommen ({inAmount}) · {outCount} hinausgetragen ({outAmount}) · {pendingCount} in keiner Meldung ({pendingAmount})',
    'boundary.colDirection': 'Richtung',
    'boundary.colOrigin': 'Aus',
    'boundary.colTarget': 'In',
    'boundary.kind.in': 'hereingenommen',
    'boundary.kind.out': 'hinausgetragen',
    'boundary.kind.pending': 'noch nirgends',
    'boundary.nowhere': 'nirgends',
    'boundary.drillTitle': 'Posten',
    'boundary.legend': 'Ein Posten, dessen eigenes Datum in einem anderen Jahr liegt, der aber in dieses übernommen wurde, steckt IN diesen Zahlen; ein hinausgetragener steckt nicht darin, obwohl sein Datum hier liegt. Ein zurückgestellter Posten ist in gar keiner Meldung — das Jahr ist erst endgültig, wenn er platziert ist. Jede Zeile öffnet den Monat, der ihn tatsächlich führt.',

    'late.title': 'Buchungen nach der Meldung {year}',
    'late.summary': '{added} neue, {changed} geänderte und {removed} stornierte Buchung(en) in den eingefrorenen Monaten {months}.',
    'late.reconstructedNote': 'Monat(e) {months} wurden aus dem ELSTER-Protokoll rekonstruiert und nicht bei der Meldung eingefroren — was vor dem Nachtrag eintraf, steckt bereits im Snapshot.',
    'late.truncated': 'Die Liste ist gekappt, es gibt weitere Zeilen.',
    'late.colKind': 'Bewegung',
    'late.colMonth': 'Monat',
    'late.colImpact': 'Kennzahl-Wirkung',
    'late.kind.added': 'neu',
    'late.kind.changed': 'geändert',
    'late.kind.removed': 'storniert',
    'late.drillTitle': 'Posten',
    'late.removedHint': 'Dieser Posten ist nicht mehr in der Liste dieses Monats — genau das heisst storniert. Sichtbar bleibt er in der eingefrorenen Liste im Posten-Tab («wie gemeldet»).',

    'checkTitle.ANNUAL_YEAR_BOUNDARY': 'Posten über die Jahresgrenze',
    'check.ANNUAL_YEAR_BOUNDARY.none': 'Es wurde kein Posten von Hand in dieses Jahr hereingenommen oder aus ihm hinausgetragen, und kein Posten dieses Jahres wartet auf eine Zuordnung.',
    'check.ANNUAL_YEAR_BOUNDARY.crossed': '{inCount} Posten mit Datum in einem anderen Jahr wurden in dieses übernommen ({inAmount}), und {outCount} Posten dieses Jahres wurden hinausgetragen ({outAmount}). Die Zahlen folgen den Entscheiden, nicht den Daten: {list}',
    'check.ANNUAL_YEAR_BOUNDARY.pending': '{pendingCount} Posten dieses Jahres sind zurückgestellt und in GAR KEINER Meldung ({pendingAmount}); {inCount} wurden hereingenommen ({inAmount}), {outCount} hinausgetragen ({outAmount}). Das Jahr ist erst endgültig, wenn jede Zurückstellung in einen Monat übernommen oder endgültig geschlossen ist.',
    'checkTitle.ANNUAL_LATE_POSTINGS': 'Buchungen nach der Meldung eines Monats dieses Jahres',
    'check.ANNUAL_LATE_POSTINGS.ok': 'In den {snapshotMonths} eingefrorenen Monaten dieses Jahres hat sich nichts bewegt.',
    'check.ANNUAL_LATE_POSTINGS.moved': '{added} neue, {changed} geänderte und {removed} stornierte Buchung(en) seit der Meldung, in Monat(en) {months} — {impact}. Was eine Kennzahl bewegt, gehört in die Jahreserklärung; sie ist die Berichtigung des Jahres.',
    'check.ANNUAL_LATE_POSTINGS.reconstructedQuiet': 'In den {snapshotMonths} eingefrorenen Monaten dieses Jahres hat sich nichts bewegt — {reconstructedCount} davon ({reconstructedMonths}) wurden allerdings aus dem ELSTER-Protokoll rekonstruiert und nicht bei der Meldung eingefroren; was vor dem Nachtrag eintraf, steckt bereits im Snapshot und ist hier nicht erkennbar.',
    'check.ANNUAL_LATE_POSTINGS.reconstructedMoved': '{added} neue, {changed} geänderte und {removed} stornierte Buchung(en) seit der Meldung, in Monat(en) {months} — {impact}. {reconstructedCount} eingefrorene Monat(e) ({reconstructedMonths}) wurden rekonstruiert statt bei der Meldung eingefroren; gezählt ist deshalb nur, was nach dem Nachtrag kam.',
    'checkTitle.ANNUAL_PREPAYMENT_TOTAL': 'Vorauszahlungssoll gegen die zwölf übermittelten Vorauszahlungen',
    'check.ANNUAL_PREPAYMENT_TOTAL.incomplete': 'Erst {filedMonths} von zwölf Monaten sind gemeldet, das Vorauszahlungssoll lässt sich noch nicht angeben; die bisher gemeldeten Kz 83 summieren sich auf {sum83}.',
    'check.ANNUAL_PREPAYMENT_TOTAL.derived': 'Die zwölf übermittelten Kz 83 summieren sich auf {sum83}, die Sondervorauszahlung (Kz 39) beträgt {sum39} — das Vorauszahlungssoll für Zeile {line} ist damit {derived}.',
    'check.ANNUAL_PREPAYMENT_TOTAL.ok': 'Das gemeldete Vorauszahlungssoll {filed} stimmt auf den Rappen mit den zwölf übermittelten Vorauszahlungen überein.',
    'check.ANNUAL_PREPAYMENT_TOTAL.off': 'Die Jahreserklärung wurde mit {filed} in Zeile {line} gemeldet, während die zwölf übermittelten Kz 83 (plus Kz 39) {derived} ergeben — eine Differenz von {gap}. Diese Zeile ist die Anrechnung des bereits Gezahlten; ein falscher Wert schlägt eins zu eins auf die Abschlusszahlung durch.',
    'checkTitle.ANNUAL_ZM_YEAR_TOTAL': 'Die ZM-Kontrollsumme des Jahres gegen Zeile 38 + Zeile 59',
    'check.ANNUAL_ZM_YEAR_TOTAL.ok': 'Die Jahreserklärung meldet {declared} innergemeinschaftliche Lieferungen; die Zusammenfassenden Meldungen des Jahres ergeben {controlTotal} über {entries} Posten — eine Differenz von {gap}. Die ZM rundet jeden Posten AUF, die Erklärung rundet eine Summe ab; eine Differenz in dieser Grössenordnung ist erwartet und vom Finanzamt akzeptiert.',
    'check.ANNUAL_ZM_YEAR_TOTAL.off': 'Die Jahreserklärung meldet {declared} innergemeinschaftliche Lieferungen, die Zusammenfassenden Meldungen des Jahres ergeben aber {controlTotal} — eine Differenz von {gap} über {entries} Posten, mehr als die Rundung je Posten öffnen kann. Die beiden Meldungen sind sich über die Lieferungen nicht einig.',
    'checkTitle.ANNUAL_FINAL_PAYMENT': 'Die Abschlusszahlung gegen das Sachkonto',
    'check.ANNUAL_FINAL_PAYMENT.zero': 'Die gemeldete Abschlusszahlung ist {expected} — es gibt nichts, das das Sachkonto ausgleichen müsste.',
    'check.ANNUAL_FINAL_PAYMENT.matched': 'Beleg {document} auf Konto {account}, gebucht am {date}: {amountLcy} = {amountEur} EUR zum Kurs jenes Monats — stimmt mit der gemeldeten Abschlusszahlung von {expected} auf den Rappen überein.',
    'check.ANNUAL_FINAL_PAYMENT.unmatched': 'Konto(en) {accounts} nach der gemeldeten Abschlusszahlung von {expected} durchsucht — {candidateCount} Kandidat(en) gefunden, keiner trifft auf wenige Rappen genau. Die Zahlung ist vielleicht noch nicht gebucht, oder liegt auf einem für diese Firma nicht konfigurierten Konto.',
    'check.ANNUAL_FINAL_PAYMENT.noAccount': 'Für diese Firma ist kein Sachkonto konfiguriert, das die Abschlusszahlung von {expected} tragen würde (VatGlAccount, Rolle \'annual\' oder \'settlement\').',
    'check.ANNUAL_FINAL_PAYMENT.monthly': 'Diese Firma begleicht die deutsche Umsatzsteuer monatlich mit dem Finanzamt, nicht mit einer Jahreszahlung — es gibt keine einzelne Buchung über {expected}, die das Sachkonto tragen müsste. Die zwölf Monatszahlungen werden hier nicht abgeglichen (Scope Phase D).',

    'status.open': 'Offen',
    'status.reviewed': 'Geprüft',
    'status.filed': 'Gemeldet',
    'status.markReviewed': 'Als geprüft markieren',
    'status.markFiled': 'Als gemeldet markieren…',
    'status.reopen': 'Wieder öffnen',
    'status.nothingFrozen': 'Noch nichts eingefroren — eine Nachbuchung in diesen Monat wäre unsichtbar.',

    'controls.title': 'Kontrollen',
    'controls.allPassed': 'alle {count} bestanden',
    'controls.needAttention': '{problems} von {count} brauchen Aufmerksamkeit',
    'controls.showAll': 'Alle Kontrollen anzeigen',
    'controls.showFindings': 'Nur Befunde anzeigen',
    'controls.everyPassed': 'Alle Kontrollen für diese Periode bestanden.',
    'controls.transactions': '{count} Buchung(en) →',

    'ustva.title': 'Umsatzsteuer-Voranmeldung — {period}',
    'ustva.kz': 'Kz',
    'ustva.label': 'Bezeichnung',
    'ustva.raw': 'Aus den Daten',
    'ustva.reported': 'Meldewert (EUR)',
    'ustva.items': 'Posten',
    'ustva.computed': 'berechnet',
    'ustva.noTransactions': 'Keine Buchungen',
    'ustva.derivedFromOthers': 'Aus den übrigen Kennzahlen abgeleitet',
    'ustva.showDerivation': 'Zeigen, wie dieser Wert zustande kommt',
    'ustva.noDerivation': 'Keine Herleitung',
    'ustva.truncatedHint': 'Bemessungsgrundlagen werden auf volle Euro abgerundet',
    'ustva.truncatedBy': '{amount} entfallen',
    'ustva.footnotePrefix': 'Kz 83 = Umsatzsteuer {outputTax}{breakdown} − Vorsteuer {deductibleTax} =',
    'ustva.footnoteSuffix': 'Bemessungsgrundlagen auf volle Euro abgerundet, Steuer aus dem gerundeten Betrag neu berechnet — so rechnet ELSTER.',
    'ustva.taxOffice': 'Finanzamt',

    'deriv.sum': 'Summe aus den Daten',
    'deriv.truncated': 'Auf volle Euro abgerundet',
    'deriv.toTheCent': 'Auf den Cent gemeldet',
    'deriv.rate': 'Steuersatz',
    'deriv.tax': 'Steuer auf die abgerundete Bemessungsgrundlage',
    'deriv.remaining': 'Verbleibende Vorauszahlung',
    'deriv.items': '({count} Posten)',
    'deriv.signReversed': 'Vorzeichen gedreht — BC bucht Verkäufe negativ',
    'deriv.dropped': '{amount} entfallen — zugunsten des Unternehmers abgerundet',
    'deriv.nothingDropped': 'nichts abzurunden',
    'deriv.inputNotRounded': 'Vorsteuer wird nie gerundet',
    'deriv.fromRoundedBase': 'aus dem gerundeten Betrag berechnet, nicht aus der von BC gebuchten Steuer — so rechnet ELSTER',
    'deriv.refund': 'negativ = Erstattung',
    'deriv.outputTaxFrom': 'Umsatzsteuer aus Kz {kz} ({base} × {rate} %)',
    'deriv.lessInput': 'abzüglich Vorsteuer Kz {kz}',

    'groups.title': 'Summen je Buchungsgruppe',
    'groups.subtitle': 'dieselbe Aufteilung wie der BC-Bericht „MWST abrechnen und buchen“',
    'groups.busGroup': 'MWST-Geschäftsbuchungsgruppe',
    'groups.prodGroup': 'MWST-Produktbuchungsgruppe',
    'groups.direction': 'Richtung',
    'groups.empty': '(leer)',
    'groups.notReported': 'nicht gemeldet',
    'groups.settlementNote': 'Die Abrechnung ist als {docs} gebucht — {amount} CHF. Sie ist aus jeder Zahl oben ausgeschlossen; mit ihr würde der Monat auf null aufgehen.',

    'gl.title': 'MWST-Sachkonten',
    'gl.subtitle': 'Bewegung ohne die Abrechnungsbuchung — das müssen die MWST-Buchungen ergeben',
    'gl.account': 'Konto',
    'gl.name': 'Bezeichnung',
    'gl.operations': 'Bewegung CHF',
    'gl.settlement': 'Abrechnung CHF',
    'gl.postings': 'Buchungen',
    'gl.showPostings': 'Buchungen anzeigen',
    'gl.openInBc': 'Die Hauptbuchposten zu diesem Beleg in Business Central öffnen',
    'gl.noDocumentNo': '(keine Belegnummer)',
    'gl.settlementExcluded': 'Abrechnung — aus der Zahl oben ausgeschlossen',
    'gl.postingCount': '{count} Buchung(en)',

    'delta.title': 'Seit der Meldung dieser Periode',
    'delta.subtitle': '{count} Buchung(en) eingefroren am {date}',
    'delta.nothingMoved': 'Nichts hat sich bewegt. Die gemeldete Erklärung stimmt weiterhin mit den Daten überein.',
    'delta.effect': 'Auswirkung auf die Meldung: {list}. Für alles, was eine Kennzahl bewegt, braucht es eine berichtigte Meldung.',
    'delta.added': 'hinzugekommen',
    'delta.changed': 'verändert',
    'delta.removed': 'entfallen',
    'delta.drillTitle': 'Buchungen {kind} seit der Meldung',

    'drill.entry': 'Lfd. Nr.',
    'drill.date': 'Datum',
    'drill.document': 'Beleg',
    'drill.type': 'Belegart',
    'drill.counterparty': 'Kunde / Lieferant',
    'drill.partnerVatId': 'USt-IdNr.',
    'drill.groups': 'Buchungsgruppen',
    'drill.baseLocal': 'Basis CHF',
    'drill.taxLocal': 'MWST CHF',
    'drill.baseReporting': 'Basis EUR',
    'drill.taxReporting': 'MWST EUR',
    'drill.loading': 'Buchungen werden geladen…',
    'drill.empty': 'Zu dieser Zahl gibt es keine Buchungen.',
    'drill.loadFailed': 'Die Buchungen konnten nicht geladen werden',
    'drill.count': '{count} Buchung(en)',
    'drill.showingFirst': '(die ersten {count} werden gezeigt)',
    'drill.settlement': '(Abrechnung)',
    'drill.scan': 'Beleg',
    'drill.noScan': 'kein Scan',
    'drill.scanTitle': 'Gescanntes Original in DocuWare (Trefferzuverlässigkeit {confidence})',
    'drill.scanUnverified': 'DocuWare-Link aus einer nicht bestätigten URL-Vorlage gebaut',
    'drill.salesNotScanned': 'Ausgangsrechnungen entstehen in BC und werden nicht in DocuWare archiviert',
    'drill.noScanLinked': 'Zu dieser Buchung ist kein DocuWare-Beleg verknüpft',

    'ec.title': 'Zusammenfassende Meldung',
    'ec.download': 'ELSTER-CSV',
    'ec.controlTotal': 'Kontrollsumme',
    'intrastat.title': 'Intrastat',
    'intrastat.download': 'IDEV-CSV',
    'intrastat.review': 'Prüfliste (xlsx)',
    'intrastat.uploadHint': 'Die IDEV-CSV so in IDEV hochladen, wie sie heruntergeladen wurde. Nicht in Excel öffnen und speichern: Excel macht aus den Codes „09“ und „08“ ein „9“ und „8“, und IDEV lehnt den Import ab. Zum Prüfen der Zeilen die Prüfliste verwenden.',
    'panel.loading': 'Wird geladen…',
    'panel.downloadFailed': 'Der Download ist fehlgeschlagen',
    'panel.notReconciled': 'Noch nicht gegen eine \u00fcbermittelte Datei abgeglichen.',
    'panel.notReconciledHint': 'Diese Zahlen entstehen auf demselben Weg wie die Erkl\u00e4rung und stimmen mit ihr \u00fcberein, aber diese Datei wurde f\u00fcr diese Firma noch nicht Zeile f\u00fcr Zeile mit dem Übermittelten verglichen. Vor dem \u00dcbermitteln pr\u00fcfen.',
    'intrastat.zeroLines': '{count} Zeile(n) ohne Menge und ohne Wert verworfen',
    'gl.kind': 'Art',
    'gl.amountChf': 'Betrag CHF',
    'ecProblem.notEuPrefix': '\u201e{p}\u201c ist kein EU-L\u00e4nderpr\u00e4fix',
    'ecProblem.ownVatId': 'das ist unsere eigene USt-IdNr.',
    'ecProblem.domesticSupply': 'eine Lieferung an eine {p}-USt-IdNr. kann keine innergemeinschaftliche Lieferung aus unserer {p}-Registrierung sein \u2014 das w\u00e4re ein steuerpflichtiger Inlandsumsatz',
    'ec.roundingNote': 'Kz 41 betr\u00e4gt {kz41}, die ZM-Kontrollsumme {controlTotal}. Die beiden sollen nicht gleich sein: Kz 41 rundet eine Summe ab, die ZM rundet jede Zeile auf. Eine Differenz von bis zu {tolerance} EUR \u00fcber {lines} Zeile(n) ist Rundung, kein Fehler.',
    'intrastat.noMovements': 'In dieser Periode gibt es keine Warenbewegungen.',
    'intrastat.missingTariff': '{count} Zeile(n) mit echter Ware haben keine Warennummer.',
    'intrastat.andMore': 'und {count} weitere',
    'intrastat.missingTariffHint': 'Sie k\u00f6nnen erst gemeldet werden, wenn die Warennummer im Artikelstamm erg\u00e4nzt ist.',
    'intrastat.excludedCharges': '{count} Kostenzeile(n) ausgeschlossen (Fracht, VOC, Zuschl\u00e4ge \u2014 keine Warenbewegungen)',
    'intrastat.cancelled': '{count} Sendung(en) im selben Monat durch eine Gutschrift aufgehoben',
    'panel.loadFailedEc': 'Die Zusammenfassende Meldung konnte nicht geladen werden',
    'panel.loadFailedIntrastat': 'Intrastat konnte nicht geladen werden',
    'panel.loadFailedReturn': 'Die Umsatzsteuer-Voranmeldung konnte nicht geladen werden',
    'status.frozenCount': '{count} Buchung(en) eingefroren',
    'status.reopenHint': 'Die Periode wieder \u00f6ffnen. Die eingefrorene Buchungsliste wird verworfen.',
    'status.changeFailed': 'Der Status konnte nicht ge\u00e4ndert werden',
    'status.fileAnyway': 'Trotzdem als gemeldet markieren?',
    'status.filedBy': 'Gemeldet am {date} von {user}',
    'status.reviewedBy': 'Gepr\u00fcft am {date} von {user}',
    'status.ticket': 'Transferticket',
    'status.stillFailing': '{count} Kontrolle(n) melden weiterhin einen Fehler. Die Meldung wird nicht blockiert, aber es bleibt festgehalten, dass der Monat mit offenen Fehlern gemeldet wurde.',
    'status.freezeExplainer': 'Die Periode als gemeldet zu markieren friert alle {count} dahinterliegenden Buchungen ein. Alles, was danach in {period} gebucht wird, erscheint anschliessend belegweise \u2014 daraus l\u00e4sst sich eine berichtigte Meldung schreiben.',
    'status.ticketUstva': 'Transferticket UStVA',
    'status.ticketZm': 'Transferticket ZM',
    'status.optional': 'optional',
    'status.note': 'Notiz',
    'status.freezeAndFile': 'Einfrieren und als gemeldet markieren',
    'status.cancel': 'Abbrechen',
    'status.reconstructedBadge': 'rekonstruiert',
    'status.reconstructedTitle': 'Aus dem ELSTER-Übertragungsprotokoll rekonstruiert, nicht beim Melden eingefroren',
    'status.reconstructedBody': 'Die Kennzahlenwerte unten sind die tatsächlich übermittelten — sie stammen aus dem Übertragungsprotokoll. Die Buchungsliste wurde erst nachträglich aus der Replika gelesen. Eine Buchung, die nach der Meldung noch in diesen Monat kam, steckt deshalb bereits darin und lässt sich nicht mehr erkennen.',
    'status.reconstructedSource': 'Quelle',
    'status.reconstructedOn': 'Gemeldet — Werte rekonstruiert am {date}',

    // ---- die Kontrollen
    'checkTitle.GL_RECONCILIATION': 'MWST-Buchungen gegen die MWST-Sachkonten',
    'checkTitle.GL_RECONCILIATION_INPUT': 'Vorsteuer: MWST-Buchungen gegen Konto {accounts}',
    'checkTitle.GL_RECONCILIATION_OUTPUT': 'Umsatzsteuer: MWST-Buchungen gegen Konto {accounts}',
    'checkTitle.EC_SALES_CONTROL_TOTAL': 'Kontrollsumme der Zusammenfassenden Meldung gegen die Erkl\u00e4rung',
    'checkTitle.UNRESOLVED_GROUPS': 'Buchungsgruppen, die keine Kennzahl erreichen',
    'checkTitle.EC_SALES_MISSING_VATID': 'Steuerfreie Lieferungen ohne USt-IdNr. des Kunden',
    'checkTitle.EC_SALES_VATID_SUSPECT': 'USt-IdNr. der Kunden',
    'checkTitle.LATE_POSTINGS': 'Buchungen, die nach der Meldung eingetroffen sind',
    'checkTitle.FILED_VS_COMPUTED': 'Gemeldet gegen das, was die Daten heute sagen',
    'checkTitle.PRIOR_MONTH': 'Ver\u00e4nderung gegen\u00fcber dem Vormonat',
    'checkTitle.ROUNDING': 'Rundung der gemeldeten Werte',
    'checkTitle.DATA_FRESHNESS': 'Datenstand der Replika',
    'checkTitle.REPORTING_CURRENCY': 'Betr\u00e4ge in der Berichtsw\u00e4hrung',

    'check.GL_RECONCILIATION.unavailable': 'Auf den konfigurierten MWST-Konten gibt es in dieser Periode keine Buchungen. Entweder ist der Monat noch nicht gebucht, oder es sind die falschen Konten hinterlegt.',
    'check.GL_RECONCILIATION_INPUT.ok': 'MWST-Buchungen und Sachkonto stimmen bei {entries} \u00fcberein (Abrechnungsbuchung auf beiden Seiten ausgeschlossen).',
    'check.GL_RECONCILIATION_INPUT.error': 'Die MWST-Buchungen sagen {entries}, das Sachkonto sagt {gl} \u2014 eine Differenz von {diff}. Es wurde entweder auf ein MWST-Konto gebucht ohne MWST-Buchung, oder eine MWST-Buchung erzeugt, ohne das Konto zu ber\u00fchren.',
    'check.GL_RECONCILIATION_OUTPUT.ok': 'MWST-Buchungen und Sachkonto stimmen bei {entries} \u00fcberein (Abrechnungsbuchung auf beiden Seiten ausgeschlossen).',
    'check.GL_RECONCILIATION_OUTPUT.error': 'Die MWST-Buchungen sagen {entries}, das Sachkonto sagt {gl} \u2014 eine Differenz von {diff}. Es wurde entweder auf ein MWST-Konto gebucht ohne MWST-Buchung, oder eine MWST-Buchung erzeugt, ohne das Konto zu ber\u00fchren.',
    'check.EC_SALES_CONTROL_TOTAL.ok': 'Die Erkl\u00e4rung meldet {reported}, die ZM-Kontrollsumme betr\u00e4gt {controlTotal}. Die Differenz von {gap} ist Rundung: die Erkl\u00e4rung rundet eine Summe ab, die ZM rundet jede der {lines} Zeilen auf. So erwartet, und vom Finanzamt akzeptiert.',
    'check.EC_SALES_CONTROL_TOTAL.error': 'Die Erkl\u00e4rung meldet {reported}, die ZM-Kontrollsumme betr\u00e4gt aber {controlTotal}. Eine Differenz von {gap} l\u00e4sst sich \u00fcber {lines} Zeile(n) nicht mit Rundung erkl\u00e4ren (h\u00f6chstens {tolerance}). Das Finanzamt gleicht genau diese beiden Werte ab.',
    'check.ecFilesUnverified': 'F\u00fcr diese Firma wird noch keine ZM-Datei angeboten, deshalb wird hier auch nicht abgestimmt. Gemessen \u00fcber 2026: die aus diesen Buchungen gebaute Meldung trifft in jedem Monat Kz 41 + Kz 42 innerhalb der zeilenweisen Rundung, die \u00fcbermittelten Dateien tun das nicht \u2014 deshalb ist ein Abgleich mit ihnen nicht der richtige Test.',
    'check.ecNoCurrency': 'Die Zusammenfassende Meldung l\u00e4sst sich f\u00fcr diese Firma nicht ableiten \u2014 ihre BC-Replika f\u00fchrt keine Fremdw\u00e4hrungsbetr\u00e4ge, und die ZM wird in Euro gemeldet.',
    'check.UNRESOLVED_GROUPS.error': '{count} Kombination(en) haben in dieser Periode Geld bewegt, erreichen aber keine Kennzahl: {list}. Jede fehlt entweder in der Regeltabelle oder ist dort als offen markiert. Nichts wird stillschweigend verworfen \u2014 gemeldet wird aber auch nichts.',
    'check.UNRESOLVED_GROUPS.ok': 'Jede Kombination, die in dieser Periode Geld bewegt hat, ist einer Kennzahl zugeordnet.',
    'check.EC_SALES_MISSING_VATID.error': '{count} Buchung(en) \u00fcber insgesamt {amount} sind als steuerfreie innergemeinschaftliche Lieferungen gebucht, tragen aber keine USt-IdNr. des Kunden. Sie k\u00f6nnen nicht in die ZM, und ohne g\u00fcltige Nummer h\u00e4lt die Steuerbefreiung nicht.',
    'check.EC_SALES_VATID_SUSPECT.error': '{list}',
    'check.EC_SALES_VATID_SUSPECT.ok': 'Alle {count} ZM-Zeile(n) tragen eine plausible EU-USt-IdNr.',
    'check.LATE_POSTINGS.ok': 'Seit der Meldung hat sich nichts bewegt: alle {snapshot} Buchung(en) sind unver\u00e4ndert.',
    'check.LATE_POSTINGS.error': '{added} neue, {changed} ver\u00e4nderte und {removed} stornierte Buchung(en) seit der Meldung \u2014 {impact}. F\u00fcr alles, was eine Kennzahl bewegt, braucht es eine berichtigte Meldung.',
    'check.LATE_POSTINGS.warning': 'Diese Periode ist als gemeldet markiert, hat aber keinen Buchungs-Snapshot \u2014 eine Nachbuchung l\u00e4sst sich damit nicht erkennen. Snapshots entstehen, sobald eine Periode \u00fcber diese Seite gemeldet wird.',
    'check.LATE_POSTINGS.reconstructedQuiet': 'Diese Periode wurde aus dem ELSTER-\u00dcbertragungsprotokoll rekonstruiert, nicht beim Melden eingefroren. Ihre {snapshot} Buchung(en) wurden erst nachtr\u00e4glich aus der Replika gelesen \u2014 was zwischen Meldung und Rekonstruktion eingetroffen ist, steckt bereits darin und l\u00e4sst sich hier nicht mehr erkennen. Seit der Rekonstruktion hat sich nichts bewegt.',
    'check.LATE_POSTINGS.reconstructedMoved': 'Diese Periode wurde aus dem ELSTER-\u00dcbertragungsprotokoll rekonstruiert, nicht beim Melden eingefroren \u2014 eine Buchung, die vor der Rekonstruktion eintraf, steckt bereits in ihren {snapshot} Buchung(en) und l\u00e4sst sich nicht erkennen. Seit der Rekonstruktion: {added} neue, {changed} ver\u00e4nderte und {removed} stornierte Buchung(en) \u2014 {impact}.',
    'check.FILED_VS_COMPUTED.ok': 'Jeder gemeldete Wert stimmt weiterhin mit den aktuellen Daten \u00fcberein.',
    'check.FILED_VS_COMPUTED.warning': 'Gemeldet / aktuell: {list}. Entweder wurde ein Wert in ELSTER von Hand angepasst, oder die zugrunde liegenden Buchungen haben sich ge\u00e4ndert.',
    'check.FILED_VS_COMPUTED.unfiledOnly': 'Jeder gemeldete Wert stimmt weiterhin — die Daten ergeben inzwischen aber zusätzlich {unfiledList}, was die Meldung gar nicht enthält. Entweder wurde nach der Meldung eine Regel aufgelöst, oder Buchungen wurden in eine Kennzahl umklassiert, die nie erklärt wurde.',
    'check.FILED_VS_COMPUTED.bothWarning': 'Gemeldet / aktuell: {list}. Zusätzlich ergeben die Daten inzwischen {unfiledList}, was die Meldung gar nicht enthält — eine nach der Meldung aufgelöste Regel, oder Buchungen, die in eine nie erklärte Kennzahl umklassiert wurden.',
    'check.PRIOR_MONTH.ok': 'Keine Kennzahl hat sich gegen\u00fcber dem Vormonat um mehr als {threshold} % bewegt.',
    'check.PRIOR_MONTH.warning': '{list}. Grosse Ausschl\u00e4ge sind in diesem Gesch\u00e4ft normal \u2014 das ist ein Anlass hinzuschauen, kein Urteil.',
    'check.ROUNDING.ok': '{list}. Bemessungsgrundlagen werden auf volle Euro abgerundet und die Steuer aus dem gerundeten Betrag neu berechnet \u2014 so rechnet ELSTER.',
    'check.DATA_FRESHNESS.ok': 'Die Replika enth\u00e4lt Buchungen bis {loadedTo}.',
    'check.DATA_FRESHNESS.error': 'Die neueste Buchung in der Replika ist vom {loadedTo} und liegt vor dem Ende dieser Periode ({periodEnd}). Der Monat ist nicht vollst\u00e4ndig geladen \u2014 auf diesen Zahlen nicht melden.',
    'check.DATA_FRESHNESS.unavailable': 'Der Ladestand der Replika liess sich nicht bestimmen.',
    'check.REPORTING_CURRENCY.unavailable': 'Die BC-Replika dieser Firma f\u00fchrt auf ihren MWST-Buchungen weder einen Belegw\u00e4hrungs- noch einen Berichtsw\u00e4hrungsbetrag, deshalb lassen sich die Euro-Kennzahlen hier nicht ableiten.',

    // ---- der Excel-artige Spaltenfilter, in jeder Posten-Tabelle
    'filter.search': 'Suchen\u2026',
    'filter.all': 'Alle',
    'filter.none': 'Keine',
    'filter.from': 'von',
    'filter.to': 'bis',
    'filter.empty': '(leer)',
    'filter.active': '{count} Filter aktiv',
    'filter.clearAll': 'Alle zur\u00fccksetzen',
    'drill.filteredCount': '{shown} von {total} Buchung(en)',

    // ---- der Tab "Mehrwertsteuer Posten" - die vollständige Grundlage der Berechnung
    'entries.viewFrozen': 'Eingefrorene Meldung',
    'entries.viewCurrent': 'Aktueller Stand',
    'entries.includeSettlement': 'Ausgleichsbuchung einschliessen',
    'entries.reconstructedBanner': 'Diese Liste wurde nachträglich aus dem ELSTER-Übertragungsprotokoll rekonstruiert, nicht beim Melden eingefroren — eine Buchung, die vor der Rekonstruktion in diesen Monat kam, steckt bereits darin und erscheint deshalb nicht als Unterschied.',
    'entries.notCaptured': 'im Freeze nicht erfasst',
    'entries.diffIntro': 'Unten farblich markiert, gegenüber der eingefrorenen Liste:',
    'entries.reportingDate': 'Meldedatum',
    'entries.postingDate': 'Buchungsdatum',
    'entries.country': 'Land',
    'entries.disposition': 'Disposition',
    'entries.settlementCol': 'Ausgleich',
    'entries.empty': 'Keine MWST-Posten für diesen Zeitraum.',
    'entries.unavailableFrozen': 'Für diese Periode liegt keine eingefrorene Buchungsliste vor — sie wurde nie über diese Seite gemeldet, oder seither wieder geöffnet.',
    'panel.loadFailedEntries': 'Die MWST-Posten konnten nicht geladen werden',

    // ---- der Tab "Einstellungen" - die fuenf Konfigurationstabellen, editierbar (Phase 4)
    'tab.settings': 'Einstellungen',
    // --- manuelle Abwahl und Nachzügler (Phase 5) ---------------------------------------
    'excl.column': 'Meldung',
    'excl.exclude': 'nicht melden',
    'excl.excludeHint': 'Diesen Posten mit Begründung aus UStVA, ZM und Intrastat dieses Monats herausnehmen.',
    'excl.reset': 'zurücknehmen',
    'excl.resetHint': 'Entscheid zurücknehmen — der Posten geht in den Monat zurück, in den sein Datum ihn stellt. Die Historie behält den Eintrag.',
    'excl.periodFiled': 'Diese Periode ist gemeldet. Ein Posten, der aus einem gemeldeten Monat heraus muss, braucht eine berichtigte Meldung, keine Abwahl.',
    'excl.periodFiledShort': 'gemeldet',
    'excl.state.deferred': 'nicht gemeldet',
    'excl.state.included': 'übernommen',
    'excl.state.dropped': 'nie melden',
    'excl.tooltipBy': '{who}, {when}, aus {period}',
    'excl.tooltipInto': 'übernommen nach {period}',
    'excl.title.exclude': 'Posten aus der Abrechnung nehmen',
    'excl.title.include': 'Posten in die Abrechnung übernehmen',
    'excl.title.drop': 'Posten endgültig nicht melden',
    'excl.title.reset': 'Entscheid zurücknehmen',
    'excl.intro.exclude': 'Der Posten bleibt in Business Central und bleibt hier sichtbar — er geht nur nicht in die Meldungen für {period}. Bitte begründen.',
    'excl.intro.include': 'Der Posten wurde in {origin} abgewählt. Er geht ab jetzt vollständig in die Meldungen für {period}.',
    'excl.intro.drop': 'Der Posten wurde in {origin} abgewählt und wird nie gemeldet. Der Entscheid und seine Begründung bleiben festgehalten.',
    'excl.intro.reset': 'Der Posten geht nach {origin} zurück, in den Monat, in den sein Datum ihn stellt. Der Entscheid bleibt in der Historie als einer, der getroffen und zurückgenommen wurde.',
    'excl.placeholder.exclude': 'z. B. noch in Abklärung mit dem Lieferanten',
    'excl.placeholder.include': 'z. B. geklärt — die Rechnung ist korrekt',
    'excl.placeholder.drop': 'z. B. Fehlbuchung, in BC storniert',
    'excl.confirm.exclude': 'Nicht melden',
    'excl.confirm.include': 'Übernehmen',
    'excl.confirm.drop': 'Endgültig nicht melden',
    'excl.confirm.reset': 'Zurücknehmen',
    'excl.cancel': 'Abbrechen',
    'excl.commentLabel': 'Begründung',
    'excl.commentHint': 'Noch {left} Zeichen. Das ist, was die Kontrolle und die Historie zeigen.',
    'excl.failed': 'Der Entscheid konnte nicht gespeichert werden.',
    'excl.filedRefusal.exclude': '{period} ist gemeldet. Ein Posten, der aus einem gemeldeten Monat heraus muss, braucht eine berichtigte Meldung, keine Abwahl.',
    'excl.filedRefusal.include': '{period} ist gemeldet und kann keinen Posten mehr aufnehmen. Bitte in einen offenen Monat übernehmen.',
    'excl.filedRefusal.drop': 'Ein gemeldeter Monat steht diesem Entscheid entgegen. Das braucht eine berichtigte Meldung, keine Abwahl.',
    'excl.filedRefusal.reset': 'Zurücknehmen würde einen gemeldeten Monat ändern (den Ursprungsmonat {origin} oder den Monat, in den der Posten übernommen wurde). Das braucht eine berichtigte Meldung.',
    'excl.package': 'Der Entscheid gilt für alle drei Meldungen gleichzeitig — UStVA, Zusammenfassende Meldung und Intrastat. Einen Posten nur aus einer davon zu nehmen, ist nicht vorgesehen.',
    'excl.effect': 'Wirkung',
    'excl.effectValue': 'Kz {kz} {sign} {amount}',
    'excl.effectNone': 'erreicht keine Kennzahl',
    'excl.signUp': 'steigt um',
    'excl.signDown': 'sinkt um',
    'excl.signBack': 'bewegt sich um',
    'excl.effectDrop': 'Kz {kz} — bleibt in keiner Meldung',
    'excl.originReason': 'Abgewählt, weil:',

    'carry.title': 'Nachzügler aus früheren Perioden',
    'carry.subtitle': '{count} offen, {amount} — der älteste seit {oldest} Monat(en)',
    'carry.origin': 'Aus',
    'carry.age': 'Alter',
    'carry.months': '{count} Mt.',
    'carry.contribution': 'Wirkung',
    'carry.comment': 'Begründung',
    'carry.actions': 'Entscheid',
    'carry.include': 'in {period} übernehmen',
    'carry.drop': 'endgültig nicht melden',
    'carry.reset': 'zurücknehmen',
    'carry.resetHint': 'Abwahl zurücknehmen — der Posten geht nach {period} zurück.',
    'carry.entryMissing': 'nicht mehr in der Replika',
    'carry.periodFiled': 'Der Monat auf dem Bildschirm ist gemeldet und kann nichts aufnehmen.',
    'carry.periodFiledShort': 'Monat gemeldet',
    'carry.readOnly': 'nur lesend',

    'checkTitle.MANUAL_EXCLUSIONS': 'Posten, die von Hand aus dieser Meldung genommen wurden',
    'check.MANUAL_EXCLUSIONS.ok': 'Aus dieser Periode wurde nichts von Hand abgewählt, und es wurde nichts in sie übernommen.',
    'check.MANUAL_EXCLUSIONS.excluded': '{excludedCount} Posten wurden von Hand aus dieser Meldung genommen, {excludedAmount} in ihren Kennzahlen: {excludedList}. Der Entscheid gilt für UStVA, ZM und Intrastat zusammen.',
    'check.MANUAL_EXCLUSIONS.carried': '{carriedCount} Posten aus einem früheren Monat wurden in diesen übernommen, {carriedAmount}: {carriedList}. Sie stecken in jeder Zahl auf dieser Seite.',
    'check.MANUAL_EXCLUSIONS.both': '{excludedCount} Posten wurden von Hand aus dieser Meldung genommen ({excludedAmount}): {excludedList}. {carriedCount} Posten aus einem früheren Monat wurden übernommen ({carriedAmount}): {carriedList}. Beide Entscheide gelten für alle drei Meldungen.',

    'checkTitle.CARRY_FORWARD_OPEN': 'Posten aus früheren Monaten, die in keiner Meldung stehen',
    'check.CARRY_FORWARD_OPEN.ok': 'Kein Posten aus einem früheren Monat wartet auf eine Meldung.',
    'check.CARRY_FORWARD_OPEN.warning': '{count} in einem früheren Monat abgewählte Posten stehen weiterhin in keiner Meldung, {amount}; der älteste wartet seit {oldest} Monat(en): {list}. Jeder muss in eine Meldung übernommen oder endgültig geschlossen werden — bis dahin wird er nirgends gemeldet.',
    'config.retroWarning': 'Diese Regeln wirken beim BERECHNEN einer Meldung, nicht beim Melden. Eine Änderung hier bewegt deshalb jeden noch offenen Monat — und ebenso die berechnete Seite jedes bereits gemeldeten Monats: eingefroren sind die Zahlen, die zum Finanzamt gingen, und die Buchungsliste, nicht der Vergleich damit. Beim Speichern steht, wie weit es zurückreicht.',
    'config.pendingHint': '{count} Buchungsgruppen-Kombination(en) stehen noch auf «pending». Sie bewegen Geld und erreichen keine Kennzahl — das wird als Befund gemeldet und nicht stillschweigend verschluckt. Sie hier aufzulösen ist der Zweck dieses Tabs.',
    'config.loadFailed': 'Die Konfiguration konnte nicht geladen werden.',
    'config.created': 'Zeile angelegt. Die Änderung steht mit Vorher/Nachher in der Historie.',
    'config.updated': 'Zeile geändert. Die Änderung steht mit Vorher/Nachher in der Historie.',
    'config.deleted': 'Zeile gelöscht. Ihr Inhalt bleibt in der Historie erhalten.',

    'config.section.line-map.title': 'Kennzahlen-Zuordnung',
    'config.section.line-map.description': 'Welche Kombination aus Geschäftsbuchungsgruppe, Produktbuchungsgruppe und Richtung in welche Kennzahl läuft. Eine Kombination ohne Zeile oder mit «pending» wird als Befund gemeldet — nie als stille Null.',
    'config.section.scope.title': 'Umfang',
    'config.section.scope.description': 'Welche Geschäftsbuchungsgruppen überhaupt zu dieser Meldung gehören. Das ist der Filter, der früher eine AutoFilter-Einstellung war.',
    'config.section.scope.warning': 'Jede MWST-Posten-Abfrage joint diese Tabelle. Eine Gruppe zu entfernen nimmt ihre Buchungen gleichzeitig aus UStVA, Zusammenfassender Meldung und Intrastat; eine hinzuzufügen bringt sie in alle drei herein.',
    'config.section.country.title': 'Meldeidentität',
    'config.section.country.description': 'Steuernummer, eigene USt-IdNr., Finanzamt, Regelsteuersatz und die vier Intrastat-Konstanten.',
    'config.section.country.warning': 'An dieser Zeile hängt die ganze Meldung: fehlt sie, kann die Seite für diese Gesellschaft nichts berechnen. Die vier Intrastat-Konstanten (11 / 3 / 08 / 99) hat Heike als immer gültig bestätigt.',
    'config.section.gl-account.title': 'MWST-Sachkonten',
    'config.section.gl-account.description': 'Die Konten, gegen die der Monat abgestimmt wird — die MWST-Posten einer Periode müssen der Bewegung auf diesen Konten entsprechen.',
    'config.section.own-registration.title': 'Eigene USt-Registrierungen',
    'config.section.own-registration.description': 'Unter welcher eigenen USt-IdNr. eine Buchungsgruppe läuft.',
    'config.section.own-registration.warning': 'Diese Tabelle ist geteilt. Der DocuWare-Bestellabgleich liest sie ebenfalls (BILLTO_VATID_MISMATCH) — eine Änderung hier verändert also auch, welche Nummer jene Prüfung auf einer Lieferantenrechnung erwartet. Ihr Schlüssel ist Gesellschaft plus Buchungsgruppe; das Land ist eine gewöhnliche Spalte, deshalb stehen hier alle Länder dieser Gesellschaft und nicht nur das oben gewählte.',

    'config.rowCount': '{count} Zeile(n)',
    'config.filteredCount': '{shown} von {total} Zeile(n)',
    'config.new': 'Neue Zeile',
    'config.empty': 'Noch keine Zeilen.',
    'config.actions': 'Aktionen',
    'config.edit': 'Bearbeiten',
    'config.duplicate': 'Auf einen anderen Schlüssel kopieren',
    'config.delete': 'Löschen',
    'config.yes': 'ja',
    'config.no': 'nein',
    'config.emptyValue': '(leer)',
    'config.notSet': 'nicht gesetzt',
    'config.emptyAllowed': 'darf leer sein',

    'config.dialogCreate': 'Neue Zeile',
    'config.dialogEdit': 'Zeile bearbeiten',
    'config.keyMarker': '(Schlüssel)',
    'config.keyLocked': 'Die Schlüsselfelder sind die Identität der Zeile und deshalb nicht änderbar. Um eine Regel auf eine andere Kombination zu verschieben: kopieren und die alte löschen.',
    'config.rulesUnmet': 'Noch nicht speicherbar',
    'config.rule.mappedNeedsKennzahl': 'Eine zugeordnete Zeile braucht eine Kennzahl — ohne sie trägt sie nichts bei, und keine Kontrolle würde das sagen.',
    'config.rule.mappedNeedsLineRole': 'Eine zugeordnete Zeile braucht eine Zeilenrolle: sie entscheidet, ob die Bemessungsgrundlage oder der Steuerbetrag gemeldet wird und wie die Steuer entsteht.',
    'config.rule.acquisitionNeedsAcqKennzahl': 'Ein innergemeinschaftlicher Erwerb braucht die Kennzahl seiner abziehbaren Seite (normalerweise 61). Business Central bucht auf diesen Posten keinen Steuerbetrag — diese Zahl entsteht nur als trunc(Basis) x Satz.',
    'config.rule.acquisitionNeedsRate': 'Ein Erwerb braucht einen Steuersatz, sonst ist seine abziehbare Seite nicht berechenbar.',
    'config.rule.outputTaxableNeedsRate': 'Eine steuerpflichtige Umsatzzeile braucht einen Steuersatz. Ohne ihn erreicht die Bemessungsgrundlage ihre Kennzahl, aber es wird keine Umsatzsteuer berechnet — Kz 83 fällt zu tief aus, ohne dass etwas darauf hinweist.',
    'config.cancel': 'Abbrechen',
    'config.save': 'Speichern',

    'config.impactTitle': 'Was diese Änderung berührt',
    'config.impactNone': 'Seit {from} keine Buchungen auf dieser Kombination. Die Änderung betrifft nichts, was bisher gebucht wurde.',
    'config.impactSummary': '{months} Monat(e) seit {from} tragen Buchungen auf dieser Kombination, insgesamt {entries} MWST-Posten: {open} noch offen, {filed} bereits gemeldet.',
    'config.impactFiledNote': 'Die gemeldeten Monate behalten die übermittelten Zahlen. Was sich für sie ändert, ist der berechnete Vergleich — «gemeldet gegen berechnet» — und genau das ist oft der Zweck der Änderung.',
    'config.impactPeriodTitle': '{entries} Posten · {status}',

    'config.historyTitle': 'Historie',
    'config.historyEmpty': 'Über diese Seite wurde noch nichts geändert. Änderungen direkt in der Datenbank werden hier absichtlich nicht erfasst — diese Spur zeigt, was die App getan hat.',
    'config.historyNoFieldChange': 'Gespeichert, ohne dass sich ein Feld geändert hat.',
    'config.historyTruncated': 'Es werden nur die neuesten Einträge gezeigt.',
    'config.action.INSERT': 'angelegt',
    'config.action.UPDATE': 'geändert',
    'config.action.DELETE': 'gelöscht',

    'config.field.companyCode': 'Gesellschaft',
    'config.field.countryCode': 'Land',
    'config.field.vatBusPostingGroup': 'Geschäftsbuchungsgruppe',
    'config.field.vatProdPostingGroup': 'Produktbuchungsgruppe',
    'config.field.direction': 'Richtung',
    'config.field.disposition': 'Disposition',
    'config.field.lineRole': 'Zeilenrolle',
    'config.field.kennzahl': 'Kennzahl',
    'config.field.kennzahlAcqTax': 'Kz Erwerbsteuer',
    'config.field.taxRatePct': 'Satz %',
    'config.field.sign': 'Vorzeichen',
    'config.field.ecSalesType': 'ZM-Art',
    'config.field.includeInIntrastat': 'Intrastat',
    'config.field.note': 'Bemerkung',
    'config.field.taxNumber': 'Steuernummer',
    'config.field.ownVatRegistrationNo': 'Eigene USt-IdNr.',
    'config.field.taxOffice': 'Finanzamt',
    'config.field.standardRatePct': 'Regelsteuersatz %',
    'config.field.intrastatNatureOfTransaction': 'Art des Geschäfts',
    'config.field.intrastatModeOfTransport': 'Verkehrszweig',
    'config.field.intrastatRegionDomestic': 'Region Inland',
    'config.field.intrastatRegionForeign': 'Region Ausland',
    'config.field.glAccountNo': 'Sachkonto',
    'config.field.accountRole': 'Kontorolle',
    'config.field.accountName': 'Kontobezeichnung',
    'config.field.vatRegistrationNo': 'USt-IdNr.',

    'config.value.direction.Einkauf': 'Einkauf',
    'config.value.direction.Verkauf': 'Verkauf',
    'config.value.disposition.mapped': 'mapped — läuft in ihre Kennzahl',
    'config.value.disposition.not_reported': 'not_reported — bewusst ausserhalb der Meldung',
    'config.value.disposition.pending': 'pending — offen, wird als Befund gemeldet',
    'config.value.lineRole.output_taxable': 'output_taxable — Basis in die Kennzahl, Steuer = trunc(Basis) x Satz',
    'config.value.lineRole.output_exempt': 'output_exempt — Basis in die Kennzahl, keine Steuer',
    'config.value.lineRole.acquisition': 'acquisition — Basis plus abziehbare Steuer gleicher Höhe',
    'config.value.lineRole.input_tax': 'input_tax — der STEUERBETRAG in die Kennzahl, abziehbar',
    'config.value.sign.1': '+1 (wie gebucht)',
    'config.value.sign.-1': '-1 (Vorzeichen drehen)',
    'config.value.ecSalesType.L': 'L — Lieferung',
    'config.value.ecSalesType.D': 'D — Dreiecksgeschäft',
    'config.value.ecSalesType.S': 'S — sonstige Leistung',
    'config.value.accountRole.input': 'Vorsteuer',
    'config.value.accountRole.output': 'Umsatzsteuer',
    'config.value.accountRole.settlement': 'Abrechnung',

    'config.deleteTitle': 'Diese Zeile löschen?',
    'config.deleteBody': 'Die Zeile wird aus der Konfiguration entfernt. In der Historie bleibt sie lesbar — das ist danach der einzige Ort, an dem ihr Inhalt noch existiert.',
    'config.deleteWarning': 'Jeder offene Monat wird ohne sie neu berechnet, und der Vergleich mit jedem gemeldeten Monat verschiebt sich. Eine Kombination, die danach gar keine Zeile mehr hat, wird als Befund gemeldet — nicht als Null.',

    // Manuelle EUR-Korrektur (Migration 229)
    'ovr.title': '{field} korrigieren',
    'ovr.local': '{field}',
    'ovr.bcValue': 'Wert BC',
    'ovr.inUse': 'Verwendeter Wert',
    'ovr.manual': 'manuell',
    'ovr.newValue': 'Neuer Wert {field} (laut Originalbeleg)',
    'ovr.notANumber': 'Keine Zahl.',
    'ovr.signFlip': 'Anderes Vorzeichen als BC. Eine Kurskorrektur behält das Vorzeichen, mit dem BC den Posten bucht.',
    'ovr.deviation': 'Abweichung zu BC: {amount} ({pct} %)',
    'ovr.commentLabel': 'Begründung',
    'ovr.commentPlaceholder': 'z.B. Kurs laut Beleg 0.9412 statt BC-Kurs',
    'ovr.commentFirst': 'Zuerst eine Begründung eingeben',
    'ovr.package': 'Der korrigierte Wert gilt für UStVA, ZM und Jahreserklärung zugleich und wird beim Melden mit dem Monat eingefroren.',
    'ovr.otherColumn': 'Kz {kz} dieses Postens liest {other}. Diese Korrektur ändert die UStVA nicht.',
    'ovr.noKennzahl': 'Dieser Posten fliesst in keine Kennzahl. Diese Korrektur ändert die UStVA nicht.',
    'ovr.readOnly': 'Nur lesen: Der Monat ist gemeldet oder du hast kein Bearbeitungsrecht.',
    'ovr.driftLong': 'BC hat sich seit der Korrektur geändert: damals {then}, heute {now}. Die Korrektur gilt weiter. Bitte prüfen, ob sie noch nötig ist.',
    'ovr.save': 'Korrektur speichern',
    'ovr.reset': 'Auf BC-Wert zurücksetzen ({bc})',
    'ovr.close': 'Schliessen',
    'ovr.failed': 'Die Korrektur konnte nicht gespeichert werden.',
    'ovr.history': 'Verlauf',
    'ovr.historyEmpty': 'Bisher keine Korrekturen.',
    'ovr.historyFailed': 'Der Verlauf konnte nicht geladen werden.',
    'ovr.historySet': 'Korrigiert {from} → {to}',
    'ovr.historyReset': 'Zurückgesetzt {from} → BC {to}',
    'ovr.historyBc': 'BC {bc}',
    'ovr.cellCorrected': 'Manuell korrigiert',
    'ovr.cellBc': 'BC: {bc}',
    'ovr.cellDrift': 'BC seither geändert: {then} → {now}',
    'ovr.cellBy': '{who}, {when}',
    'ovr.cellEditHint': 'Euro-Wert manuell korrigieren',
};
